import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import { trackLead } from './metaPixel'
import SchoolDisclosure from './SchoolDisclosure'

// HomePros recruiting page (/join). Full React port of the standalone design.
// All CSS is scoped under `.rec-root` so the recruiting page's generic class
// names (.btn, .card, .hero, header, etc.) and its dark theme never leak into
// the rest of the Hub. Built section by section — hero first.

// Josh's TidyCal booking page. Shown ONLY on the application result (#apply),
// after the save succeeds; every other "book / qualify" CTA goes to #apply.
const BOOKING_URL = 'https://tidycal.com/joshmarquez/the-path-to-100'

// ── APPLICATION (#apply) — edit these to change the questions or the routing ──
// Answer labels are saved exactly as written here (recruit_leads.application
// and the FUB note), so renaming one changes what new applications record.
const APPLY_STATUS_OPTIONS = ['Licensed in Arizona', 'Newly licensed (last 12 months)', 'Licensed in another state']
const APPLY_CLOSINGS_OPTIONS = ['0', '1–5', '6–11', '12–24', '25+']
const APPLY_VOLUME_OPTIONS = ['Under $1M', '$1M–$3M', '$3M–$6M', '$6M+']
const APPLY_WORKLOAD_OPTIONS = ['Full-time', 'Part-time']
const APPLY_PATH_OPTIONS = [
  { value: 'Stay a REALTOR® (HomePros)', title: 'Stay a REALTOR®', sub: 'HomePros' },
  { value: 'Go independent (HomePros Advantage, MLS Choice)', title: 'Go independent', sub: 'HomePros Advantage · MLS Choice' },
  { value: 'Not sure yet', title: 'Not sure yet', sub: "We'll walk through both" },
]
const APPLY_LANGUAGE_OPTIONS = ['English', 'Spanish', 'Both']

// Routing. PART-TIME wins over any production. Otherwise a full-time agent is a
// PRODUCER at or above EITHER threshold below; everyone else full-time is SPLIT.
const APPLY_PRODUCER_MIN_CLOSINGS = '12–24'   // this bucket or higher
const APPLY_PRODUCER_MIN_VOLUME = '$3M–$6M'   // this bucket or higher
const APPLY_TRACKS = {
  producer: { title: 'Performance 100% track', booking: true },
  split: { title: 'Split + Guidance track', booking: true },
  parttime: { title: 'Part-time', booking: false },   // shows APPLY_PART_TIME_MESSAGE, no booking link
}
const APPLY_PART_TIME_MESSAGE = "Thanks, we have your info. Part-time agents join on our Split + Guidance terms. Josh will review and reach out if it's a fit."
const APPLY_BOOKING_LABEL = 'Book your call with Josh'

// Shown beside the optional SMS checkbox, and saved with the application
// word for word when it is checked.
const APPLY_SMS_CONSENT_TEXT = 'I agree to receive calls and text messages from HomePros at the mobile number above about joining HomePros, including by automated means. Consent is not a condition of applying. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.'

function applyTrack(a) {
  if (a.workload === 'Part-time') return 'parttime'
  const atLeast = (list, value, min) => list.indexOf(value) >= list.indexOf(min)
  if (atLeast(APPLY_CLOSINGS_OPTIONS, a.closings, APPLY_PRODUCER_MIN_CLOSINGS) ||
      atLeast(APPLY_VOLUME_OPTIONS, a.volume, APPLY_PRODUCER_MIN_VOLUME)) return 'producer'
  return 'split'
}

export default function RecruitLanding() {
  const [menuOpen, setMenuOpen] = useState(false)
  const rootRef = useRef(null)
  const heroCanvasRef = useRef(null)
  const brainRef = useRef(null)
  const mathRef = useRef(null)

  // ── Scroll-reveal: fade/slide sections in as they enter the viewport ────────
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const els = root.querySelectorAll('.reveal')
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'))
      return
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])

  // ── Hero "intelligence network" canvas animation ───────────────────────────
  // Ported verbatim from the design's vanilla-JS engine. Runs only while the
  // canvas is on-screen (IntersectionObserver) and honors reduced-motion.
  useEffect(() => {
    const canvas = heroCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Read per-resize rather than captured once at mount. Dragging the window to
    // a display with a different pixel ratio changes it, and a value sampled at
    // mount would leave the canvas soft for the rest of the session.
    let dpr = 0
    let W = 0, H = 0, cx = 0, cy = 0, t = 0, raf = null, running = false
    const labels = ['MLS', 'Comps', 'Market', 'Trends', '$/Sqft', 'BINSR']
    let nodes = []
    const pulses = []

    function build() {
      nodes = []
      const n = labels.length
      const R = Math.min(W, H) * 0.34
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2
        nodes.push({ ang: a, rad: R * (0.85 + Math.random() * 0.28), label: labels[i], phase: Math.random() * Math.PI * 2, spawn: Math.floor(Math.random() * 160) })
      }
    }
    function resize() {
      const r = canvas.getBoundingClientRect()
      // A box under a pixel means the element is not laid out yet. Sizing the
      // backing store to 1px and letting CSS stretch it back up IS the blur, so
      // nothing is drawn until there is a real box — the observer below fires
      // again the moment there is one.
      if (r.width < 1 || r.height < 1) return
      const nextDpr = Math.min(window.devicePixelRatio || 1, 2)
      if (r.width === W && r.height === H && nextDpr === dpr) return
      const sizeChanged = r.width !== W || r.height !== H
      W = r.width; H = r.height; dpr = nextDpr
      // Rounded, not floored: a fractional box (a 1.5x display, a zoomed page)
      // truncated down leaves the backing store a hair small and CSS scales it
      // up, which is the same softness in miniature.
      canvas.width = Math.max(1, Math.round(W * dpr))
      canvas.height = Math.max(1, Math.round(H * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      cx = W * 0.5; cy = H * 0.5
      // Rebuild only on a genuine size change. Radii scale with the box, but
      // build() also re-randomises every node — doing that for a DPR-only
      // change would make the network jump for no visible reason.
      if (sizeChanged || nodes.length === 0) build()
      // Repaint now. The loop covers this while it runs, but under reduced
      // motion, or while the canvas is off-screen, nothing else will and the
      // canvas would sit blank or stale until something started it.
      if (!running) draw()
    }
    function nodePos(nd) {
      const wob = Math.sin(t * 0.02 + nd.phase) * 6
      return { x: cx + Math.cos(nd.ang) * (nd.rad + wob), y: cy + Math.sin(nd.ang) * (nd.rad + wob) }
    }
    function draw() {
      t++
      ctx.clearRect(0, 0, W, H)
      let i, pos
      for (i = 0; i < nodes.length; i++) {
        pos = nodePos(nodes[i])
        ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(pos.x, pos.y); ctx.lineTo(cx, cy); ctx.stroke()
        if (!reduce) { nodes[i].spawn--; if (nodes[i].spawn <= 0) { pulses.push({ nd: nodes[i], p: 0 }); nodes[i].spawn = 140 + Math.floor(Math.random() * 150) } }
      }
      for (let j = pulses.length - 1; j >= 0; j--) {
        const pu = pulses[j]; pu.p += 0.013; if (pu.p >= 1) { pulses.splice(j, 1); continue }
        const np = nodePos(pu.nd); const x = np.x + (cx - np.x) * pu.p, y = np.y + (cy - np.y) * pu.p
        const col = (j % 3 === 0) ? '#fdbf00' : '#147efb'
        ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = 10
        ctx.beginPath(); ctx.arc(x, y, 2.4, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0
      }
      ctx.font = '600 10px "Space Mono", monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      for (let k = 0; k < nodes.length; k++) {
        const p2 = nodePos(nodes[k])
        ctx.fillStyle = 'rgba(20,126,251,0.9)'; ctx.beginPath(); ctx.arc(p2.x, p2.y, 3.4, 0, Math.PI * 2); ctx.fill()
        const lx = cx + Math.cos(nodes[k].ang) * (nodes[k].rad + 24), ly = cy + Math.sin(nodes[k].ang) * (nodes[k].rad + 24)
        ctx.fillStyle = 'rgba(200,200,212,0.5)'; ctx.fillText(nodes[k].label, lx, ly)
      }
      const pulse = 0.5 + 0.5 * Math.sin(t * 0.05)
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 44 + pulse * 8)
      grd.addColorStop(0, 'rgba(20,126,251,0.5)'); grd.addColorStop(1, 'rgba(20,126,251,0)')
      ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(cx, cy, 44 + pulse * 8, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#0B0B0F'; ctx.strokeStyle = '#147efb'; ctx.lineWidth = 1.5
      ctx.beginPath(); ctx.arc(cx, cy, 20, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
      ctx.fillStyle = '#fdbf00'; ctx.font = '700 11px "Space Mono", monospace'; ctx.fillText('AI', cx, cy)
    }
    function loop() { if (!running) { raf = null; return } draw(); raf = requestAnimationFrame(loop) }

    window.addEventListener('resize', resize)

    // THE ACTUAL FIX for the blurry hero. The canvas is a position:absolute
    // inset:0 child of the hero, so its final box is decided by layout AFTER
    // this effect runs. Measuring once at mount caught a pre-layout box — which
    // is why it looked stretched on wide screens and fine on a phone, where the
    // first measurement happened to be close enough. A ResizeObserver fires as
    // soon as the real box exists and on every later change, including ones no
    // window resize event reports: fonts settling, the hero's grid resolving,
    // an image above it loading and pushing the layout.
    let ro = null
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => resize())
      ro.observe(canvas)
    }
    // Still called directly, for browsers without ResizeObserver and because
    // the observer's first callback is a frame away. resize() early-returns
    // when nothing changed, so the two never duplicate work.
    resize()

    let io = null
    if (reduce) {
      draw()
    } else if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((es) => {
        es.forEach((e) => {
          running = e.isIntersecting
          if (running && !raf) raf = requestAnimationFrame(loop)
        })
      })
      io.observe(canvas)
    } else {
      running = true; raf = requestAnimationFrame(loop)
    }

    return () => {
      running = false
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      if (ro) ro.disconnect()
      if (io) io.disconnect()
    }
  }, [])

  // ── Tech section "two-brain" live data-flow diagram (SVG) ───────────────────
  // Ported verbatim from the design's SVG engine. Pulses travel from the three
  // source nodes into the AI core (which pulses on arrival), then from the core
  // out to the report node (which lights up). Runs only while on-screen; all
  // timers/observers/RAF are torn down on unmount.
  useEffect(() => {
    const brain = brainRef.current
    if (!brain) return
    const svg = brain.querySelector('.brain-links')
    if (!svg) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const SVGNS = 'http://www.w3.org/2000/svg'
    const q = (id) => brain.querySelector('#' + id)
    const links = [
      { from: 'bn-armls', to: 'bn-core', color: '#147efb' },
      { from: 'bn-broker', to: 'bn-core', color: '#fdbf00' },
      { from: 'bn-appraiser', to: 'bn-core', color: '#fdbf00' },
      { from: 'bn-core', to: 'bn-report', color: '#147efb', out: true },
    ]
    const pulses = []
    let raf = null, running = false, started = false
    const timers = []

    function edge(id, side) {
      const el = q(id), br = brain.getBoundingClientRect(), r = el.getBoundingClientRect()
      const cxp = r.left - br.left + r.width / 2, cyp = r.top - br.top + r.height / 2
      if (side === 'right') return { x: r.right - br.left, y: cyp }
      if (side === 'left') return { x: r.left - br.left, y: cyp }
      if (side === 'top') return { x: cxp, y: r.top - br.top }
      if (side === 'bottom') return { x: cxp, y: r.bottom - br.top }
      return { x: cxp, y: cyp }
    }
    function layout() {
      const br = brain.getBoundingClientRect()
      const mob = window.matchMedia('(max-width:820px)').matches
      svg.setAttribute('viewBox', '0 0 ' + br.width + ' ' + br.height)
      while (svg.firstChild) svg.removeChild(svg.firstChild)
      links.forEach((lk) => {
        if (mob) { lk._a = edge(lk.from, 'bottom'); lk._b = edge(lk.to, 'top') }
        else { lk._a = edge(lk.from, 'right'); lk._b = edge(lk.to, 'left') }
        const ln = document.createElementNS(SVGNS, 'line')
        ln.setAttribute('x1', lk._a.x); ln.setAttribute('y1', lk._a.y)
        ln.setAttribute('x2', lk._b.x); ln.setAttribute('y2', lk._b.y)
        ln.setAttribute('stroke', mob ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.09)'); ln.setAttribute('stroke-width', '1.5')
        svg.appendChild(ln)
      })
    }
    function pulseCore() { const c = q('bn-core'); if (!c) return; c.classList.remove('pulse'); void c.offsetWidth; c.classList.add('pulse') }
    function litReport() { const rp = q('bn-report'); if (!rp) return; rp.classList.add('lit'); timers.push(setTimeout(() => rp.classList.remove('lit'), 1100)) }
    function spawn(lk) {
      const c = document.createElementNS(SVGNS, 'circle')
      c.setAttribute('r', '3.6'); c.setAttribute('fill', lk.color); c.setAttribute('cx', lk._a.x); c.setAttribute('cy', lk._a.y)
      c.setAttribute('opacity', '0.98'); c.style.filter = 'drop-shadow(0 0 5px ' + lk.color + ')'
      svg.appendChild(c)
      pulses.push({ lk, p: 0, el: c })
    }
    function frame() {
      if (!running) { raf = null; return }
      for (let i = pulses.length - 1; i >= 0; i--) {
        const pu = pulses[i]; pu.p += 0.017
        if (pu.p >= 1) { if (pu.lk.out) litReport(); else pulseCore(); if (pu.el.parentNode) svg.removeChild(pu.el); pulses.splice(i, 1); continue }
        const a = pu.lk._a, b = pu.lk._b
        pu.el.setAttribute('cx', a.x + (b.x - a.x) * pu.p)
        pu.el.setAttribute('cy', a.y + (b.y - a.y) * pu.p)
      }
      raf = requestAnimationFrame(frame)
    }
    function startSpawns() {
      if (started) return; started = true
      const ins = links.filter((l) => !l.out)
      ins.forEach((lk, idx) => {
        timers.push(setTimeout(() => { if (running) spawn(lk) }, idx * 520))
        timers.push(setInterval(() => { if (running) spawn(lk) }, 2500))
      })
      const out = links.filter((l) => l.out)[0]
      timers.push(setTimeout(() => { if (running) spawn(out) }, 1500))
      timers.push(setInterval(() => { if (running) spawn(out) }, 2500))
    }

    window.addEventListener('resize', layout)
    let io = null
    if (reduce) {
      layout(); litReport()
    } else if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((es) => {
        es.forEach((e) => {
          if (e.isIntersecting) { layout(); running = true; if (!raf) raf = requestAnimationFrame(frame); startSpawns() }
          else { running = false }
        })
      }, { threshold: 0.2 })
      io.observe(brain)
    } else {
      layout(); running = true; startSpawns(); raf = requestAnimationFrame(frame)
    }

    return () => {
      running = false
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('resize', layout)
      if (io) io.disconnect()
      timers.forEach((t) => { clearTimeout(t); clearInterval(t) })
    }
  }, [])

  // ── Lead-gen calculator ─────────────────────────────────────────────────────
  // Ported verbatim from the design's vanilla engine (inputs stay uncontrolled;
  // the engine reads .value and renders the source rows + SVG pie directly).
  // The only change vs. the standalone page: the lead-capture "send" posts to the
  // send-recruit-lead-to-fub edge function via supabase.functions.invoke instead
  // of a raw fetch. Fully torn down on unmount (StrictMode-safe).
  useEffect(() => {
    const scope = mathRef.current
    if (!scope) return
    const q = (id) => scope.querySelector('#' + id)
    const listeners = []
    const on = (el, type, fn) => { if (!el) return; el.addEventListener(type, fn); listeners.push([el, type, fn]) }

    const gNum = (id) => { const el = q(id); const v = el ? parseFloat(el.value) : 0; return isFinite(v) ? v : 0 }
    const gFmt = (n) => (n || 0).toLocaleString('en-US')

    const G_SOURCES = [
      { key: 'referral', name: 'Referrals', mix: 25, conv: 12, color: '#147efb' },
      { key: 'database', name: 'Database / SOI', mix: 25, conv: 8, color: '#fdbf00' },
      { key: 'paid', name: 'Portals / paid', mix: 20, conv: 4, color: '#4ad07d' },
      { key: 'organic', name: 'Organic online', mix: 15, conv: 2, color: '#a879ff' },
    ]
    const G_OTHER = { name: 'Other', conv: 3, color: '#3a3a44' }
    let gLastResult = {}

    const gFind = (k) => { for (let i = 0; i < G_SOURCES.length; i++) { if (G_SOURCES[i].key === k) return G_SOURCES[i] } return null }
    const gOtherMix = () => { let a = 0; for (let i = 0; i < G_SOURCES.length; i++) { a += G_SOURCES[i].mix } return Math.max(0, 100 - a) }

    function gRenderSources() {
      const box = q('g_sources'); if (!box) return; box.innerHTML = ''
      G_SOURCES.forEach((s) => {
        const row = document.createElement('div'); row.className = 'gsrc'
        row.innerHTML =
          '<span class="nm">' + s.name + '</span>' +
          '<span class="cv"><input type="number" id="conv-' + s.key + '" value="' + s.conv + '" /><span>% conv</span></span>' +
          '<input type="range" min="0" max="100" step="1" id="mix-' + s.key + '" value="' + s.mix + '" aria-label="' + s.name + ' share of deals" />'
        box.appendChild(row)
        on(scope.querySelector('#mix-' + s.key), 'input', (e) => { gSetMix(s.key, parseFloat(e.target.value) || 0) })
        on(scope.querySelector('#conv-' + s.key), 'input', (e) => { s.conv = Math.max(0.1, parseFloat(e.target.value) || 0); gCalc() })
      })
    }
    function gSetMix(key, val) {
      let others = 0; G_SOURCES.forEach((s) => { if (s.key !== key) others += s.mix })
      val = Math.max(0, Math.min(val, 100 - others))
      const src = gFind(key); if (src) src.mix = val
      const el = scope.querySelector('#mix-' + key); if (el) el.value = String(val)
      gCalc()
    }

    function gCalc() {
      const gci = gNum('g_gci'), asp = gNum('g_asp'), comm = gNum('g_comm') / 100, split = gNum('g_split') / 100
      const net = asp * comm * split
      const closings = net > 0 ? Math.ceil(gci / net) : 0
      const mixList = []; G_SOURCES.forEach((s) => { mixList.push({ name: s.name, color: s.color, mix: s.mix, conv: s.conv }) })
      mixList.push({ name: G_OTHER.name, color: G_OTHER.color, mix: gOtherMix(), conv: G_OTHER.conv })
      let totalLeads = 0; const leadsBySource = []
      mixList.forEach((m) => {
        const cl = closings * (m.mix / 100)
        const ld = m.conv > 0 ? Math.ceil(cl / (m.conv / 100)) : 0
        totalLeads += ld; leadsBySource.push({ name: m.name, color: m.color, leads: ld })
      })
      const perMonth = Math.ceil(totalLeads / 12)
      q('g_close').textContent = closings ? gFmt(closings) : '—'
      q('g_leads').textContent = totalLeads ? gFmt(totalLeads) : '—'
      q('g_month').textContent = perMonth ? gFmt(perMonth) : '—'
      q('g_alloc').textContent = (100 - gOtherMix()) + '%'
      q('g_othpct').textContent = gOtherMix() + '%'
      gDrawPie(leadsBySource)
      gLastResult = { gciGoal: gci, closings, totalLeads, leadsPerMonth: perMonth }
    }

    function gDrawPie(data) {
      const svg = q('g_pie'), legend = q('g_legend')
      if (!svg) return; svg.innerHTML = ''; if (legend) legend.innerHTML = ''
      let total = 0; data.forEach((d) => { total += d.leads })
      const R = 15.9155, C = 2 * Math.PI * R
      if (total <= 0) {
        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        bg.setAttribute('cx', '18'); bg.setAttribute('cy', '18'); bg.setAttribute('r', String(R))
        bg.setAttribute('fill', 'none'); bg.setAttribute('stroke', '#1C1C22'); bg.setAttribute('stroke-width', '4')
        svg.appendChild(bg); return
      }
      let off = 0
      data.forEach((d) => {
        if (d.leads <= 0) return
        const frac = d.leads / total, len = frac * C
        const seg = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        seg.setAttribute('cx', '18'); seg.setAttribute('cy', '18'); seg.setAttribute('r', String(R))
        seg.setAttribute('fill', 'none'); seg.setAttribute('stroke', d.color); seg.setAttribute('stroke-width', '4')
        seg.setAttribute('stroke-dasharray', len + ' ' + (C - len))
        seg.setAttribute('stroke-dashoffset', String(-off))
        svg.appendChild(seg); off += len
        if (legend) { const it = document.createElement('span'); it.innerHTML = '<i style="background:' + d.color + '"></i>' + d.name + ' (' + Math.round(frac * 100) + '%)'; legend.appendChild(it) }
      })
    }

    // conversion mode toggle + actuals
    const def = q('g_mode_def'), act = q('g_mode_act'), box = q('g_actuals')
    if (def && act && box) {
      on(def, 'click', () => { def.classList.add('on'); act.classList.remove('on'); box.classList.remove('show') })
      on(act, 'click', () => { act.classList.add('on'); def.classList.remove('on'); box.classList.add('show') })
    }
    const apply = q('g_apply')
    on(apply, 'click', () => {
      const c = gNum('g_ac'), l = gNum('g_al'), out = q('g_acmsg')
      if (c > 0 && l > 0) {
        const rate = (c / l) * 100
        G_SOURCES.forEach((s) => { s.conv = rate; const el = scope.querySelector('#conv-' + s.key); if (el) el.value = rate.toFixed(1) })
        G_OTHER.conv = rate
        if (out) out.textContent = rate.toFixed(1) + '% applied'
        gCalc()
      } else if (out) { out.textContent = 'Enter closings & leads > 0' }
    })

    ;['g_gci', 'g_asp', 'g_comm', 'g_split'].forEach((id) => { on(q(id), 'input', gCalc) })

    // lead capture -> Follow Up Boss via send-recruit-lead-to-fub edge function
    const btn = q('g_send')
    on(btn, 'click', () => {
      const name = (q('g_name').value || '').trim()
      const email = (q('g_email').value || '').trim()
      const phone = (q('g_phone').value || '').trim()
      const msg = q('g_capmsg')
      const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
      const phoneOk = (phone.replace(/\D/g, '').length >= 10)
      if (!name) { msg.className = 'gcap-msg err'; msg.textContent = 'Add your name so we know who to reach out to.'; return }
      if (!emailOk && !phoneOk) { msg.className = 'gcap-msg err'; msg.textContent = 'Add your email or phone so we can reach you.'; return }

      // Map the calculator result → the function's `calculator` field (readable
      // label:value pairs that land in the FUB note). source is omitted so the
      // function defaults to "HomePros Recruiting" (drives the FUB Lead Flow).
      const calculator = {
        'Annual GCI goal': '$' + gFmt(gLastResult.gciGoal),
        'Closings needed / year': gFmt(gLastResult.closings),
        'Total leads required': gFmt(gLastResult.totalLeads),
        'Leads per month': gFmt(gLastResult.leadsPerMonth),
      }
      const payload = { name, email, phone, calculator }

      msg.className = 'gcap-msg'; msg.textContent = 'Sending…'
      const ok = () => { msg.className = 'gcap-msg ok'; msg.textContent = 'Got it — your info is saved and someone from HomePros will reach out shortly.'; trackLead() }
      const fail = () => { msg.className = 'gcap-msg err'; msg.textContent = 'Something went wrong — call us at (602) 693-0218.' }
      supabase.functions
        .invoke('send-recruit-lead-to-fub', { body: payload })
        .then(({ data, error }) => {
          if (error || (data && data.error)) { console.error('[recruit lead] failed:', error || data.error); fail() }
          else ok()
        })
        .catch((e) => { console.error('[recruit lead] threw:', e); fail() })
    })

    gRenderSources()
    gCalc()

    return () => {
      listeners.forEach(([el, type, fn]) => el.removeEventListener(type, fn))
      const sb = q('g_sources'); if (sb) sb.innerHTML = ''
      const lg = q('g_legend'); if (lg) lg.innerHTML = ''
      const pie = q('g_pie'); if (pie) pie.innerHTML = ''
    }
  }, [])

  // FAQ accordion toggle — same behavior as the design (each item opens/closes
  // independently; answer animates open to its measured height).
  const toggleFaq = (e) => {
    const qEl = e.currentTarget.closest('.q')
    const ans = qEl.querySelector('.ans')
    const isOpen = qEl.getAttribute('data-open') === 'true'
    qEl.setAttribute('data-open', String(!isOpen))
    e.currentTarget.setAttribute('aria-expanded', String(!isOpen))
    ans.style.maxHeight = isOpen ? '0px' : (ans.scrollHeight + 'px')
  }

  return (
    <div className="rec-root" ref={rootRef}>
      <style>{css}</style>

      {/* ============ HEADER ============ */}
      <header>
        <div className="wrap nav">
          <a href="#top" className="logo"><img className="logo-img" src="/homepros-logo.png" alt="HomePros" /></a>
          <nav className="nav-links">
            <a href="#tech">Technology</a>
            <a href="#paths">Two Paths</a>
            <a href="#offer">What You Get</a>
            <a href="#math">The Math</a>
            <a href="#plans">Plans</a>
            <a href="#team">Leadership</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav-right">
            <a className="btn btn-primary btn-sm" href="#apply">Apply now</a>
            <a className="btn btn-ghost btn-sm nav-call" href="tel:6026930218">(602) 693-0218</a>
            <button className="menu-btn" aria-label="Menu" onClick={() => setMenuOpen(o => !o)}>
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
        <div className={menuOpen ? 'mobile-menu open' : 'mobile-menu'}>
          <a href="#tech" onClick={() => setMenuOpen(false)}>Technology</a>
          <a href="#apply" onClick={() => setMenuOpen(false)}>Apply now</a>
          <a href="#paths" onClick={() => setMenuOpen(false)}>Two Paths</a>
          <a href="#offer" onClick={() => setMenuOpen(false)}>What You Get</a>
          <a href="#math" onClick={() => setMenuOpen(false)}>The Math</a>
          <a href="#plans" onClick={() => setMenuOpen(false)}>Plans</a>
          <a href="#team" onClick={() => setMenuOpen(false)}>Leadership</a>
          <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
          <a href="tel:6026930218" onClick={() => setMenuOpen(false)}>Call (602) 693-0218</a>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="hero" id="top">
        <div className="hero-grid-bg"></div>
        <div className="hero-glow"></div>
        <div className="wrap">
          <div className="hero-inner">
            <div className="hero-left">
              <span className="eyebrow">Realtors® & MLS Choice · Phoenix, AZ</span>
              <h1>We build <span className="swipe">entrepreneurs</span>, not passengers.</h1>
              <p className="lede">HomePros is Arizona's tech-first brokerage — real broker support, elite training, and the freedom to keep NAR or go independent. Your career, your terms.</p>
              <div className="hero-cta">
                <a className="btn btn-primary" href="#apply">Apply now <span className="arrow">→</span></a>
                <a className="btn btn-ghost" href="#tech">See the technology</a>
                <a className="btn btn-ghost" href="tel:6026930218">Call us · (602) 693-0218</a>
              </div>
              <div className="trust">
                <span><b>21+</b> years of experience</span>
                <span className="tick">✓ <b style={{ marginLeft: '2px' }}>REALTORS®</b> & MLS Choice</span>
                <span className="tick">✓ Real broker support, <b style={{ marginLeft: '2px' }}>24/7</b></span>
                <span><b>AI tools</b> built in-house</span>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hv-badge"><span className="live"></span> LIVE INTELLIGENCE</div>
              <canvas ref={heroCanvasRef} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE TECHNOLOGY ============ */}
      <section className="section tech-sec" id="tech">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">The technology</span>
            <h2>Everyone rents the same software.<br />We built the part you can't rent.</h2>
            <p>Let's be honest — the CRM everyone brags about, Follow Up Boss with Zillow Pro built in, we give you that too. It's the best in the business and we didn't invent it. What we <em>did</em> build is a layer of AI tools built on how a 21-year broker prices and how appraisers choose comps. We built our own.</p>
          </div>

          {/* signature: two-brain live data flow */}
          <div className="brain reveal" id="brain" ref={brainRef}>
            <svg className="brain-links" aria-hidden="true"></svg>
            <div className="brain-col brain-in">
              <div className="bnode" id="bn-armls">
                <div className="bn-h"><span className="bdot live"></span> MLS · LIVE</div>
                <div className="bn-s">Direct MLS connection — active comps and solds in real time, with the agent-side detail consumer sites never show.</div>
              </div>
              <div className="bnode" id="bn-broker">
                <div className="bn-h"><span className="bdot g"></span> Broker · 21+ yrs</div>
                <div className="bn-s">The exact comp parameters top agents actually price with.</div>
              </div>
              <div className="bnode" id="bn-appraiser">
                <div className="bn-h"><span className="bdot g"></span> Appraiser's discipline</div>
                <div className="bn-s">Condition · size · recency · concessions · FHA · VA · conventional · cash</div>
              </div>
            </div>
            <div className="brain-col brain-core">
              <div className="bcore spin" id="bn-core">
                <div className="bcore-ring"></div>
                <div className="bcore-label">HomePros AI</div>
                <div className="bcore-sub">Strategy Assistant</div>
              </div>
              <div className="bcore-tag"><span className="ad"></span> Powered by Anthropic's Claude</div>
            </div>
            <div className="brain-col brain-out">
              <div className="bnode bout" id="bn-report">
                <div className="brep"><span className="brep-l"></span><span className="brep-l"></span><span className="brep-l s"></span><span className="brep-l"></span><span className="brep-l s"></span></div>
                <div className="bn-h">Branded client report</div>
                <div className="bn-s">Client-ready pricing strategy, in your name — in minutes.</div>
              </div>
            </div>
          </div>
          <p className="brain-cap"><b>Connected live to the MLS</b>, processed by Anthropic's Claude — two brains and a live feed, every time you run a number.</p>

          <div className="tools">

            {/* 01 — Strategy Assistant */}
            <div className="tool reveal">
              <div className="tool-copy">
                <div className="tool-num">01 — Strategy Assistant</div>
                <h3>Live MLS comps with a broker's judgment <em>and</em> an appraiser's discipline.</h3>
                <p className="one">Not a Zestimate. Not an automated AVM. Not a generic AI wrapper. It's a <b>direct connection to the MLS</b> — the full agent-side record, not the scrubbed version consumer sites show — and it prices the way your best agents do.</p>
                <p className="body">Two brains in one tool: a broker with 21+ years in the business, running comps with the exact parameters agents actually use — plus an appraiser's discipline: comps sorted by condition, held to size and recency limits, and read net of seller concessions. It knows how every comp was financed — FHA, VA, conventional or cash — and flags appraisal risk before your buyer finds it. And it doesn't stop at comps. It hands you a report branded to you, ready to walk your seller through real pricing strategy.</p>
                <ul>
                  <li><span className="ck">✓</span> Connected live to the MLS — current listings and solds, not estimates</li>
                  <li><span className="ck">✓</span> Appraisal-style comp selection: condition, size, recency, net of concessions</li>
                  <li><span className="ck">✓</span> Broker strategy layer — priced the way top agents price</li>
                  <li><span className="ck">✓</span> Branded client report, ready in minutes</li>
                </ul>
                <p className="mock-note" style={{ textAlign: 'left' }}>A pricing and research tool for licensed agents, not an appraisal. You remain responsible for your own work, and your designated broker supervises it.</p>
                <a className="btn btn-ghost" href="tel:6026930218">See it live on a call <span className="arrow">→</span></a>
              </div>
              <div className="tool-visual">
                <div className="browser">
                  <div className="browser-bar"><span className="d r"></span><span className="d y"></span><span className="d g"></span><div className="url">truetopproducer.com/strategy</div></div>
                  <div className="browser-body">
                    <div className="mock">
                      <div className="mock-rhead"><span className="mock-brand">HomePros<span className="mdot"></span></span><span className="mock-tag">Comparative Market Strategy</span></div>
                      <div className="mock-subj">Subject · 4521 W Cactus Rd, Glendale AZ</div>
                      <div className="mock-rows">
                        <div className="mock-row"><span>Comp 1 · 0.3 mi · sold</span><b>$438,000</b></div>
                        <div className="mock-row"><span>Comp 2 · 0.5 mi · sold</span><b>$451,500</b></div>
                        <div className="mock-row"><span>Comp 3 · 0.4 mi · pending</span><b>$445,000</b></div>
                      </div>
                      <div className="mock-reco"><span>Recommended list price</span><b>$450,000</b></div>
                      <div className="mock-note">condition-adjusted · broker strategy · live MLS · illustrative</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 02 — BINSR Assistant */}
            <div className="tool reveal reverse">
              <div className="tool-copy">
                <div className="tool-num">02 — BINSR Assistant</div>
                <h3>Turn a messy inspection into a clean, professional <em>BINSR</em> — in minutes.</h3>
                <p className="one">The inspection response is where deals get tense and hours disappear. The BINSR Assistant drafts it for you — <b>on both sides of the table.</b></p>
                <p className="body">Representing the buyer? It drafts the inspection notice straight from the report, with a Drafting mode and a Walk-through mode. Representing the seller? It drafts your response. Either way, it comes out organized, professional, and in the right Arizona format — so you look sharp, protect the deal, and keep it moving instead of staring at a blank page.</p>
                <ul>
                  <li><span className="ck">✓</span> Buyer-side drafting from the inspection — Drafting &amp; Walk-through modes</li>
                  <li><span className="ck">✓</span> Seller-side response drafting</li>
                  <li><span className="ck">✓</span> Clean, professional language that protects the transaction</li>
                  <li><span className="ck">✓</span> Built for Arizona contracts and the BINSR form</li>
                </ul>
                <a className="btn btn-ghost" href="tel:6026930218">See it live on a call <span className="arrow">→</span></a>
              </div>
              <div className="tool-visual">
                <div className="browser">
                  <div className="browser-bar"><span className="d r"></span><span className="d y"></span><span className="d g"></span><div className="url">truetopproducer.com/binsr</div></div>
                  <div className="browser-body">
                    <div className="mock">
                      <div className="mock-rhead"><span className="mock-brand">HomePros<span className="mdot"></span></span><span className="mock-tag">BINSR · Buyer's Notice (draft)</span></div>
                      <div className="mock-items">
                        <div className="mock-item"><span className="mk">1</span><p>HVAC — service unit and provide certification of proper operation.</p></div>
                        <div className="mock-item"><span className="mk">2</span><p>Roof — repair flashing at chimney penetration per inspection §4.2.</p></div>
                        <div className="mock-item"><span className="mk">3</span><p>Electrical — install / repair GFCI outlets at kitchen and exterior.</p></div>
                      </div>
                      <div className="mock-note">drafted in your voice · Arizona BINSR format · edit &amp; send · illustrative</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* THE FULL TOOLKIT. Everything else agents get, below the two
              tools shown in depth above. Same .card / .grid-3 as What You
              Get, and the .tool-num label the tool blocks use. Casita Check and
              Pipeline span two columns so the AI tools fill two full rows.
              Market Snapshot is left out while it is paused for agents. Public
              copy says "MLS", never "ARMLS". */}
          <div className="toolkit">
            <div className="head reveal"><span className="eyebrow">The full toolkit</span></div>
          <div className="toolkit-group">
            <div className="tool-num reveal">AI tools</div>
            <div className="grid-3">
              <div className="card wide reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12l6-5 6 5" /><path d="M4 10.5V20h8v-9.5" /><path d="M14 20h7v-6l-3.5-3-3.5 3" /></svg></div>
                <h3>Casita Check</h3>
                <p>Enter an address and see if the lot allows a casita. Pulls parcel data straight from Maricopa County records, checks each city's casita rules and Luke Air Force Base zones, and gives your client a bilingual report.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M8 13h8M8 17h5" /></svg></div>
                <h3>Deal Desk</h3>
                <p>An AI drafting assistant for contracts and counteroffers, built on Arizona forms. You review every word, and your broker is one call away.</p>
              </div>
              <div className="card wide reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h10M4 18h6" /><path d="M17 15l2 2 3-4" /></svg></div>
                <h3>Pipeline</h3>
                <p>AI on top of Follow Up Boss. Ask who to call today, find past clients, and line up tasks, with nothing sent without your OK.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /><path d="M10.2 9.5a2 2 0 1 1 2.8 1.8c-.6.3-1 .8-1 1.4v.3" /><path d="M12 16h.01" /></svg></div>
                <h3>Ask Ada</h3>
                <p>Instant answers on Arizona licensing and real estate rules, from the team that owns the school.</p>
              </div>
            </div>
          </div>
          <div className="toolkit-group">
            <div className="tool-num reveal">Done for you</div>
            <div className="grid-3">
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg></div>
                <h3>Weekly emails</h3>
                <p>Fresh client emails written for you, in English and Spanish. Copy, send, and stay top of mind.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><rect x="6" y="7" width="6" height="5" /><path d="M14 8h4M14 11h4M6 15h12" /></svg></div>
                <h3>Content</h3>
                <p>Ready-to-share market articles with images, built from current stats across multiple market sources.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3v2a3 3 0 0 1-3 3" /><path d="M7 5H4v2a3 3 0 0 0 3 3" /></svg></div>
                <h3>My Wins</h3>
                <p>Close a deal and a branded social card is ready automatically, pulled from the MLS.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg></div>
                <h3>Link page</h3>
                <p>Your own link-in-bio page that works like a funnel: it captures home valuation requests and shows off your closed deals.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-7 9 7" /><path d="M5 9.5V20h14V9.5" /><path d="M10 20v-5h4v5" /></svg></div>
                <h3>Open house</h3>
                <p>An editable page for every open house you host. Visitors sign in, and they land in your CRM.</p>
              </div>
              <div className="card reveal">
                <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" /><path d="M15 9a4 4 0 0 1 0 6" /><path d="M18 6a8 8 0 0 1 0 12" /></svg></div>
                <h3>Broker updates, events &amp; vendors</h3>
                <p>Direct updates from your broker, team events, and a vetted vendor list in one place.</p>
              </div>
            </div>
          </div>
          </div>

          {/* The toolkit above summarises every tool; the product site shows
              them working, so this still points there. Apply now sits beside
              it: the application is the next section, so this is the hand-off. */}
          <div className="tools-more reveal">
            <a className="btn btn-ghost" href="https://www.truetopproducer.com" target="_blank" rel="noopener noreferrer">
              See all the tools <span className="arrow">→</span>
            </a>
            <a className="btn btn-primary" href="#apply">Apply now <span className="arrow">→</span></a>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ APPLY ============ */}
      <ApplySection />

      <hr className="divider" />

      {/* ============ TWO PATHS ============ */}
      <section className="section" id="paths">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">Two entities · One team</span>
            <h2>Leave NAR? Stay NAR?<br />You decide — the support never changes.</h2>
            <p>Most agents don't need a new broker. They need options. HomePros runs two entities under one roof so you keep control of your dues, your forms, and your business — with the same people in your corner either way.</p>
          </div>

          <div className="fork">
            <div className="path left reveal">
              <span className="tag">HomePros</span>
              <h3>Stay a REALTOR®</h3>
              <p className="who">For agents who want full NAR benefits and association tools.</p>
              <ul>
                <li><Check color="#147EFB" /> Full MLS &amp; Supra access</li>
                <li><Check color="#147EFB" /> AAR forms &amp; contracts</li>
                <li><Check color="#147EFB" /> Full NAR member benefits</li>
                <li><Check color="#147EFB" /> Complete HomePros tech &amp; training</li>
              </ul>
              <div className="foot"><a className="btn btn-ghost" href="#apply">Apply now</a><a className="btn btn-ghost" href="tel:6026930218">Call (602) 693-0218</a></div>
            </div>

            <div className="fork-split"><div className="vline"></div><div className="or">OR</div></div>

            <div className="path right reveal">
              <span className="tag">HomePros Advantage</span>
              <h3>Go independent</h3>
              <p className="who">For non-NAR agents who want MLS access without national dues — via the MLS Choice program.</p>
              <ul>
                <li><Check color="#FDBF00" /> Full MLS &amp; Supra access</li>
                <li><Check color="#FDBF00" /> Industry-standard forms &amp; contracts</li>
                <li><Check color="#FDBF00" /> No NAR &amp; no AAR dues</li>
                <li><Check color="#FDBF00" /> Same tech, same training, same team</li>
              </ul>
              <div className="foot"><a className="btn btn-gold" href="#apply">Apply now</a><a className="btn btn-ghost" href="tel:6026930218">Call (602) 693-0218</a></div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ WHAT YOU GET ============ */}
      <section className="section" id="offer">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">The ecosystem</span>
            <h2>Everything you need to run your business — in one place.</h2>
            <p>Other brokerages hand you a login and wish you luck. We hand you a system, real guidance, and a creative team. Fewer scattered tools, more closings.</p>
          </div>

          <div className="grid-3">
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" /><path d="M3 9h18M8 21h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></div>
              <h3>Integrated tech stack</h3>
              <p>Follow Up Boss — the CRM built by Zillow — with <b style={{ color: '#D9D9DE', fontWeight: 600 }}>Zillow Pro</b> inside it: real buyer-intent signals, AI-recommended follow-up, and a Premium Agent Profile that keeps you in front of clients on Zillow. Plus transaction and compliance management, all connected.</p>
            </div>
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3L2 8l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M6 10.5V16c0 1.5 3 3 6 3s6-1.5 6-3v-5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></div>
              <h3>Training that compounds</h3>
              <p>Every HomePros agent attends classes at <b style={{ color: '#D9D9DE', fontWeight: 600 }}>Avance School of Real Estate</b> free — contracts, negotiation, branding and CE, year-round. We teach it, test it, and help you live it.</p>
            </div>
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M15 8l6-3v14l-6-3M3 7h9a2 2 0 012 2v6a2 2 0 01-2 2H3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg></div>
              <h3>In-house media team</h3>
              <p>Closing-day videos, short-form reels and branded content through <b style={{ color: '#D9D9DE', fontWeight: 600 }}>MediaPros</b>. We don't just market properties — we market <em>you</em>.</p>
            </div>
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2a7 7 0 00-4 12.7V17a1 1 0 001 1h6a1 1 0 001-1v-2.3A7 7 0 0012 2z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M9 21h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg></div>
              <h3>Broker support, 24/7</h3>
              <p>Approachable, accessible brokers who actually pick up. New agent or 20-year veteran, you'll never be stuck on a contract at 9pm again.</p>
            </div>
            {/* A "30% off your license" card stood here, advertising the same
                ASREB / FastClass partnership as the band below. Both went with
                the offer; the My Wins card at the end of this grid took the
                slot, so the three-up stays full rather than wrapping 3 + 2. */}
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3l7 3v5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6l7-3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
              <h3>Compliance-first</h3>
              <p>Your license is your livelihood. Every week in <b style={{ color: '#D9D9DE', fontWeight: 600 }}>Lead Lab</b>, Josh works through selling, contracts, and the compliance and law that keep you clean and licensed — so you grow without stepping on a landmine.</p>
            </div>
            {/* The trophy is the Hub's own My Wins glyph, so this card and the
                rail row an agent will actually click read as one thing. Stroke
                sits on the svg rather than repeated across five paths — the
                other cards here put it on a single path each, which does not
                scale to a five-path mark. */}
            <div className="card reveal">
              <div className="ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3v2a3 3 0 0 1-3 3" /><path d="M7 5H4v2a3 3 0 0 0 3 3" /></svg></div>
              <h3>Wins that market themselves</h3>
              <p>The moment you list, go pending, or close, your post is already designed and waiting. <b style={{ color: '#D9D9DE', fontWeight: 600 }}>My Wins</b> turns a closed deal into a branded social card automatically — no design tool, no waiting on anyone, and no closing that quietly goes unmarketed because you were busy starting the next one.</p>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ THE MATH / CALCULATOR ============ */}
      <section className="section calc-sec" id="math" ref={mathRef}>
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">Your GCI is math, not luck</span>
            <h2>Know your number before you chase it.</h2>
            <p>Plug in your income goal and see exactly how many closings, leads, and monthly conversations it takes. Then let's build the plan to hit it.</p>
          </div>

          <div className="gcalc reveal">
            {/* 01 GOALS */}
            <div className="gpanel">
              <div className="gpanel-h"><b>01</b> Your goals</div>
              <div className="gpanel-b">
                <div className="gfield"><label htmlFor="g_gci">Annual GCI goal</label><div className="grow"><span className="sym">$</span><input id="g_gci" type="number" inputMode="numeric" defaultValue="200000" /></div></div>
                <div className="gtwo">
                  <div className="gfield"><label htmlFor="g_asp">Avg sale price</label><div className="grow"><span className="sym">$</span><input id="g_asp" type="number" inputMode="numeric" defaultValue="450000" /></div></div>
                  <div className="gfield"><label htmlFor="g_comm">Commission</label><div className="grow"><input id="g_comm" type="number" step="0.1" inputMode="decimal" defaultValue="2.5" /><span className="sym">%</span></div></div>
                </div>
                <div className="gfield"><label htmlFor="g_split">Your split %</label><div className="grow"><input id="g_split" type="number" inputMode="decimal" defaultValue="90" /><span className="sym">%</span></div></div>
                <div className="gfield">
                  <label>Conversion source</label>
                  <div className="gseg"><button type="button" id="g_mode_def" className="on">Industry defaults</button><button type="button" id="g_mode_act">My last 12 months</button></div>
                </div>
                <div className="gactuals" id="g_actuals">
                  <div className="gtwo">
                    <div className="gfield"><label htmlFor="g_ac">Closings (12m)</label><div className="grow"><input id="g_ac" type="number" inputMode="numeric" defaultValue="0" /></div></div>
                    <div className="gfield"><label htmlFor="g_al">Leads (12m)</label><div className="grow"><input id="g_al" type="number" inputMode="numeric" defaultValue="0" /></div></div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><button type="button" className="gbtn-sm" id="g_apply">Use this rate</button><span className="gsmall" id="g_acmsg">—</span></div>
                  <span className="gsmall">Applies your real blended rate across every source.</span>
                </div>
              </div>
            </div>

            {/* 02 PROJECTIONS */}
            <div className="gpanel">
              <div className="gpanel-h"><b>02</b> Your projections</div>
              <div className="gpanel-b">
                <div className="gout b1"><div className="l">Closings needed / year</div><div className="v" id="g_close">—</div></div>
                <div className="gout b2"><div className="l">Total leads required</div><div className="v" id="g_leads">—</div></div>
                <div className="gout b3"><div className="l">Leads per month</div><div className="v" id="g_month">—</div></div>
                <details className="gnote">
                  <summary>Conversion benchmarks by source</summary>
                  <ul>
                    <li><b>Past client / referral:</b> highest intent — 10–30%.</li>
                    <li><b>Database / SOI:</b> 5–10%+ with consistent cadence.</li>
                    <li><b>Portals (Zillow / Realtor.com):</b> ~3–5%; top teams 7–9%.</li>
                    <li><b>Paid online (PPC / Meta / SEO):</b> ~1–2.5%; 5%+ with elite follow-up.</li>
                    <li><b>Pro move:</b> use your own last 12 months and re-forecast quarterly.</li>
                  </ul>
                </details>
              </div>
            </div>

            {/* 03 LEAD MIX */}
            <div className="gpanel">
              <div className="gpanel-h"><b>03</b> Your lead mix</div>
              <div className="gpanel-b">
                <div className="gpie-wrap"><svg className="gpie" id="g_pie" viewBox="0 0 36 36" role="img" aria-label="Lead volume by source"></svg></div>
                <div className="glegend" id="g_legend"></div>
                <div id="g_sources"></div>
                <div className="gother"><span>Allocated <b id="g_alloc" style={{ color: 'var(--bone)' }}>85%</b></span><span>Other (auto) <b id="g_othpct" style={{ color: 'var(--bone)' }}>15%</b></span></div>
              </div>
            </div>
          </div>

          {/* LEAD CAPTURE */}
          <div className="gcapture reveal">
            <div>
              <h3>Put your numbers in front of HomePros.</h3>
              <p>Leave your info and we'll reach out to help you turn these numbers into a real pipeline. Every agent is different — so we build it with you, not from a template.</p>
              <div className="gcap-msg" id="g_capmsg"></div>
            </div>
            <div className="gcap-form">
              <input id="g_name" type="text" placeholder="Your name" autoComplete="name" />
              <input id="g_email" type="email" placeholder="Email" autoComplete="email" />
              <input id="g_phone" type="tel" placeholder="Phone" autoComplete="tel" />
              <button type="button" className="btn btn-gold" id="g_send">Have HomePros reach out</button>
            </div>
          </div>

          <div className="calc-cta">
            <span className="calc-cta-or">Prefer to talk now?</span>
            <a className="btn btn-ghost" href="tel:6026930218">Call us directly · (602) 693-0218</a>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ PLANS ============ */}
      <section className="section" id="plans">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">Compensation</span>
            <h2>We reward producers. Plainly.</h2>
            <p>We're not a "100% brokerage" that abandons you at the door. We offer a 100% <em>structure</em> you earn through production — and a straightforward split, with real guidance, for agents still growing. No junk fees. No fine print.</p>
          </div>

          <div className="plans">
            <div className="plan reveal">
              <span className="pk">Growing agents</span>
              <h3>Split + Guidance</h3>
              <p className="sub">For agents still building. A straightforward split, plus the tools and the weekly guidance to get you to the 100% structure.</p>
              <div className="line"></div>
              <ul>
                <li><Check color="#147EFB" /> Weekly Lead Lab — selling, contracts &amp; compliance</li>
                <li><Check color="#147EFB" /> Full tech stack &amp; free training</li>
                <li><Check color="#147EFB" /> A clear path to the 100% structure</li>
              </ul>
              <a className="btn btn-ghost" href="tel:6026930218">Talk it through</a>
            </div>

            <div className="plan feature reveal">
              <span className="badge">Top producers</span>
              <span className="pk">Producer phase</span>
              <h3>Performance 100%</h3>
              <p className="sub">For agents who hit our production criteria. Keep what you earn — freedom you earned through results, not promises.</p>
              <div className="line"></div>
              <ul>
                <li><Check color="#FDBF00" /> 100% structure for qualifying agents</li>
                <li><Check color="#FDBF00" /> Full tech, media &amp; broker support</li>
                <li><Check color="#FDBF00" /> Priority access to the leadership team</li>
              </ul>
              <a className="btn btn-primary" href="#apply">See if you qualify <span className="arrow">→</span></a>
            </div>
          </div>
          <p style={{ color: 'var(--muted-2)', fontSize: '13px', marginTop: '22px', textAlign: 'center' }}>Exact fees and thresholds are tailored to your experience, production, and the support you want. We'll build your plan on the call.</p>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ LEADERSHIP ============ */}
      <section className="section" id="team">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">The team behind the desk</span>
            <h2>Brokers, educators, and a creative team — invested in you.</h2>
          </div>
          <div className="team">
            <div className="member reveal">
              <div className="photo-wrap"><picture><source type="image/webp" srcSet="/team/josh-800.webp" /><img className="photo" src="/team/josh-800.jpg" alt="Josh Marquez" width="800" height="800" loading="lazy" decoding="async" /></picture></div>
              <h3>Josh Marquez</h3>
              <div className="role">CEO / Designated Broker · HomePros</div>
              <p>21+ years in Arizona real estate and a certified CE instructor at Avance School of Real Estate. Josh built HomePros to pair hands-on broker support with useful tech agents actually use, the kind most brokerages can't match.</p>
              <div className="chips"><span className="chip">Designated Broker · HomePros</span><span className="chip">CE Instructor · Avance School</span><span className="chip">21+ years in Arizona real estate</span></div>
            </div>
            <div className="member reveal">
              <div className="photo-wrap"><picture><source type="image/webp" srcSet="/team/danae-800.webp" /><img className="photo" src="/team/danae-800.jpg" alt="Danae Marquez" width="800" height="800" loading="lazy" decoding="async" /></picture><img className="cred-badge" src="/team/uofa-dms-badge.png" alt="University of Arizona Digital Marketing Science certification" width="96" height="96" loading="lazy" decoding="async" /></div>
              <h3>Danae Marquez</h3>
              <div className="role">Designated Broker · HomePros Advantage</div>
              <p>Danae covers contracts and marketing. As a designated broker she knows the forms cold, and she makes sure what you post fits Arizona advertising rules.</p>
              <div className="chips"><span className="chip">Digital Marketing Science, University of Arizona</span><span className="chip">BA Government, minor in Business, GCU</span><span className="chip">Arizona Real Estate Broker</span></div>
            </div>
            <div className="member reveal">
              <div className="photo-wrap"><picture><source type="image/webp" srcSet="/team/madeline-800.webp" /><img className="photo" src="/team/madeline-800.jpg" alt="Madeline Marquez" width="800" height="800" loading="lazy" decoding="async" /></picture></div>
              <h3>{'Madeline "Maddy" Marquez'}</h3>
              <div className="role">Videographer · MediaPros</div>
              <p>Maddy shoots and cuts the content that markets you: listing tours, closing videos, podcast highlights, and reels. Her real estate license means she knows what buyers look for.</p>
              <div className="chips"><span className="chip">Licensed AZ real estate agent</span><span className="chip">Videographer &amp; editor · MediaPros</span></div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ TESTIMONIALS ============ */}
      <section className="section" id="stories">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">From our agents</span>
            <h2>Support that feels like family. Numbers that don't lie.</h2>
          </div>
          <div className="quotes">
            <div className="quote reveal">
              <div className="mark">“</div>
              <p>Ten years in real estate, and my two years with HomePros have been transformational. From day one I felt valued as an agent, not a number — cutting-edge tech, accessible brokers, top-tier training, and resources that actually grow your business. If you want a brokerage that supports you like family, talk to Josh and Danae.</p>
              <div className="by"><div className="av">DR</div><div><b>David Ramos Ocasio</b><span>Licensed REALTOR® · 10+ years</span></div></div>
            </div>
            <div className="quote reveal">
              <div className="mark">“</div>
              <p>The best brokerage I've worked with in over a decade. It's not just fair compensation — it's the unmatched 24/7 broker support, the front-and-back-end platforms, and the best CRM in the industry. The ongoing education and marketing strategy have been invaluable to my growth.</p>
              <div className="by"><div className="av">LO</div><div><b>Luis Ornelas</b><span>Licensed REALTOR® · 10+ years</span></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ EDUCATION BAND ============ */}
      {/* Was the FastClass band — 30% off pre-licensing through a partner. That
          offer is gone, and this is the stronger statement anyway: the discount
          was somebody else's, and this is ours. No .off number here on purpose;
          "free" is the whole claim and a percentage would undersell it. */}
      <section className="section-sm">
        <div className="wrap">
          <div className="band reveal">
            <div>
              <span className="eyebrow">Keep your license current</span>
              <h3>Your CE classes are free. We own the school.</h3>
              <p>Every HomePros agent takes continuing education at <b style={{ color: '#D9D9DE', fontWeight: 600 }}>Avance School of Real Estate</b> at no cost — contracts, negotiation, fair housing, and the hours the state actually asks for. Not a partner discount. Our school, our instructors, included with the brokerage.</p>
            </div>
            <div className="actions">
              <a className="btn btn-ghost" href="https://www.avanceschool.com" target="_blank" rel="noopener noreferrer">Visit Avance School</a>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* ============ FAQ ============ */}
      <section className="section" id="faq">
        <div className="wrap">
          <div className="head reveal">
            <span className="eyebrow">Straight answers</span>
            <h2>Frequently asked questions</h2>
          </div>
          <div className="faq reveal">
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>Do you offer a 100% commission structure?</span><span className="plus"></span></button>
              <div className="ans"><p>We're not a "100% brokerage." We offer a 100% <b style={{ color: '#D9D9DE' }}>structure</b> for producing agents who meet specific production criteria. Still building? Come in on a split, with guidance through our weekly Lead Lab, and work your way there. We believe in earning your freedom through production — not promises.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>Can I join without being a REALTOR® (NAR member)?</span><span className="plus"></span></button>
              <div className="ans"><p>Yes — through <b style={{ color: '#D9D9DE' }}>HomePros Advantage</b>. NAR rules prevent NAR-affiliated brokerages from hiring non-REALTOR® agents, so we created a second entity for non-NAR agents. Same systems, same support, same people. Different structure — same mission.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>What technology and tools do agents get?</span><span className="plus"></span></button>
              <div className="ans"><p>Two layers. First, the tools every serious agent should have: Follow Up Boss for CRM, BoldTrail Back Office for transactions and back office, and <b style={{ color: '#D9D9DE' }}>Zillow Pro</b> built right into Follow Up Boss. Then the part no other brokerage has — <b style={{ color: '#D9D9DE' }}>TrueTopProducer</b>, our own platform: AI assistants (Strategy and BINSR), lead-gen funnels, Open House funnels, auto-generated deal cards, and ready-to-post marketing content delivered on a constant basis. Plus our in-house MediaPros team for video, reels, and branded content.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>What are your compensation options?</span><span className="plus"></span></button>
              <div className="ans"><p>Two paths, kept simple. A Performance 100% structure for agents who meet our production criteria — and for agents still growing, a split with real guidance through our weekly Lead Lab. We'll name the exact numbers with you on a call, matched to your experience and production. No hidden fees.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>Do you provide training and mentorship?</span><span className="plus"></span></button>
              <div className="ans"><p>Constantly. We own <b style={{ color: '#D9D9DE' }}>Avance School of Real Estate</b>, which offers CE and non-CE classes year-round — and every HomePros agent attends free. Contracts, marketing, negotiation, branding, business growth. We don't just talk about success; we teach it and test it.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>What kind of agents do you look for?</span><span className="plus"></span></button>
              <div className="ans"><p>Producers. Agents who show up, execute, and treat real estate like a business. If you're disciplined, hungry and open to feedback, you'll thrive here. We build professionals, not passengers.</p></div>
            </div>
            <div className="q" data-open="false">
              <button aria-expanded="false" onClick={toggleFaq}><span>What are your fees?</span><span className="plus"></span></button>
              <div className="ans"><p>Fees depend on your chosen program and production level. We tailor your plan to your goals, experience and desired support level. No junk fees, no fine print — just transparent, scalable programs built for growth. We'll walk through the exact numbers on your call.</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="final section">
        <div className="glow"></div>
        <div className="wrap">
          <span className="eyebrow" style={{ justifyContent: 'center' }}>This is HomePros</span>
          <h2>Ready to build like a professional?</h2>
          <p>Book a private strategy session with Josh — no pressure, just a real conversation about your business. And yes — the coffee's on Josh.</p>
          <div className="final-cta">
            <a className="btn btn-primary" href="#apply">Apply now <span className="arrow">→</span></a>
            <a className="btn btn-ghost" href="tel:6026930218">Call us · (602) 693-0218</a>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="wrap">
          <div className="foot-top">
            <div className="foot-brand">
              <a href="#top" className="logo"><img className="logo-img foot-logo-img" src="/homepros-logo.png" alt="HomePros" /></a>
              <p>Arizona's tech-first brokerage. Real mentorship, industry-leading technology, and a creative team that markets you like our own brand.</p>
              <div className="socials">
                <a href="https://www.facebook.com/HomeProsArizona" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M13 22v-9h3l.5-3.5H13V7.3c0-1 .3-1.7 1.8-1.7H17V2.4C16.6 2.4 15.3 2.3 13.9 2.3c-3 0-5 1.8-5 5.1v2.1H6V13h2.9v9H13z" /></svg></a>
                <a href="https://www.instagram.com/homeprosarizona/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" /><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" /><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" /></svg></a>
                <a href="https://www.youtube.com/@HomePros" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M23 12s0-3.2-.4-4.7a2.5 2.5 0 00-1.8-1.8C19.3 5 12 5 12 5s-7.3 0-8.8.5A2.5 2.5 0 001.4 7.3C1 8.8 1 12 1 12s0 3.2.4 4.7a2.5 2.5 0 001.8 1.8C4.7 19 12 19 12 19s7.3 0 8.8-.5a2.5 2.5 0 001.8-1.8C23 15.2 23 12 23 12zM10 15.5v-7l6 3.5-6 3.5z" /></svg></a>
              </div>
            </div>
            <div className="foot-col">
              <h4>Company</h4>
              <a href="#paths">Two Paths</a>
              <a href="#offer">What You Get</a>
              <a href="#team">Leadership</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="foot-col">
              <h4>Get started</h4>
              <a href="https://www.avanceschool.com" target="_blank" rel="noopener noreferrer">Avance School · free CE</a>
              <a href="tel:6026930218">Call us · (602) 693-0218</a>
              <a href="#paths">Leaving NAR?</a>
              <a href="#math">Lead-gen calculator</a>
            </div>
            <div className="foot-col">
              <h4>Contact</h4>
              <a href="tel:6026930218">(602) 693-0218</a>
              <a href="mailto:broker@homeprosarizona.com">broker@homeprosarizona.com</a>
              <span>9059 W McDowell Rd, Suite 200<br />Tolleson, AZ 85353</span>
              <span>Mon–Fri · 9:00am–6:00pm</span>
            </div>
          </div>
          {/* School identification. Sits between .foot-top and .compliance,
              outside both, so neither block's grid applies to it. 28px matches
              .compliance's own padding-top — the one value that governs spacing
              in this band — and .compliance contributes that 28px again below,
              so the disclosure reads as its own row rather than a stray line
              attached to the legal text. Inline-styled and font-inheriting, so
              it picks up Inter from --body here. */}
          <div style={{marginTop:'28px',marginBottom:'28px'}}>
            <SchoolDisclosure variant="dark" />
          </div>
          <div className="compliance">
            <p className="legal">
              <b>HomePros</b> — Designated Broker: Josh Marquez. &nbsp;|&nbsp; <b>HomePros Advantage</b> — Designated Broker: Danae Marquez. &nbsp;|&nbsp; 9059 W McDowell Rd, Suite 200, Tolleson, AZ 85353. &nbsp;|&nbsp; Licensed by the Arizona Department of Real Estate. Equal Housing Opportunity.
            </p>
            <p className="copy">© 2026 HomePros.<br />All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

// Check-mark bullet used across the recruiting page (color varies by path).
function Check({ color }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M13 4L6 12L3 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ── APPLICATION ─────────────────────────────────────────────────────────────
// Four steps, then a result. Saved through send-recruit-lead-to-fub, which
// writes the recruit_leads backup row first and then posts to FUB. The result
// (and the booking link) appear only after that save succeeds.
//
// Controlled React state, unlike the calculator's vanilla engine, so the
// prerender renders step 1 and crawlers see the heading and the first question.
const APPLY_EMPTY = {
  status: '', closings: '', volume: '', workload: '', path: '', language: '',
  first: '', last: '', phone: '', email: '', brokerage: '', smsConsent: false,
}

function ApplySection() {
  const [step, setStep] = useState(1)
  const [a, setA] = useState(APPLY_EMPTY)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)   // track key, set only after a successful save

  const set = (field, value) => setA((prev) => ({ ...prev, [field]: value }))

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email.trim())
  const phoneOk = a.phone.replace(/\D/g, '').length >= 10
  const answered = {
    1: !!a.status,
    2: !!a.closings && !!a.volume && !!a.workload,
    3: !!a.path && !!a.language,
    4: !!a.first.trim() && !!a.last.trim() && phoneOk && emailOk,
  }

  async function submit() {
    if (!answered[4] || sending) return
    setSending(true); setError('')
    const track = applyTrack(a)
    const payload = {
      first_name: a.first.trim(),
      last_name: a.last.trim(),
      email: a.email.trim(),
      phone: a.phone.trim(),
      application: {
        status: a.status,
        closings: a.closings,
        volume: a.volume,
        workload: a.workload,
        path: a.path,
        languages: [a.language],   // one answer; an array so the stored shape stays the same
        current_brokerage: a.brokerage.trim(),
        sms_consent: a.smsConsent,
        sms_consent_text: a.smsConsent ? APPLY_SMS_CONSENT_TEXT : null,
        track,
      },
    }
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('send-recruit-lead-to-fub', { body: payload })
      if (fnErr || (data && data.error)) throw fnErr || new Error(data.error)
      trackLead()
      setResult(track)
    } catch (e) {
      console.error('[recruit application] failed:', e)
      setError('Something went wrong — call us at (602) 693-0218.')
    } finally {
      setSending(false)
    }
  }

  const choice = (on) => 'apply-opt' + (on ? ' on' : '')
  const seg = (list, value, onPick) => (
    <div className="gseg apply-seg">
      {list.map((opt) => (
        <button type="button" key={opt} className={value === opt ? 'on' : ''} aria-pressed={value === opt} onClick={() => onPick(opt)}>{opt}</button>
      ))}
    </div>
  )

  return (
    <section className="section apply-sec" id="apply">
      <div className="wrap">
        <div className="apply-grid">
          <div className="apply-intro reveal">
            <span className="eyebrow">Apply to HomePros</span>
            <h2>Start your path to 100.</h2>
            <ol className="apply-next">
              <li><b>01</b><span>Answer four quick questions.</span></li>
              <li><b>02</b><span>We match you to a track: Performance 100% or Split + Guidance.</span></li>
              <li><b>03</b><span>Talk with Josh about your numbers.</span></li>
              <li><b>04</b><span>Get your plan, with the exact terms in writing.</span></li>
            </ol>
            <p className="apply-conf">100% confidential. Your current broker will never know you applied.</p>
          </div>

          <div className="gpanel apply-card reveal">
            {result ? (
              <div className="gpanel-b apply-result" aria-live="polite">
                {APPLY_TRACKS[result].booking ? (
                  <>
                    <p className="apply-lede">Thanks, we have your info.</p>
                    <h3>{APPLY_TRACKS[result].title}</h3>
                    <a className="btn btn-primary" href={BOOKING_URL} target="_blank" rel="noopener noreferrer">{APPLY_BOOKING_LABEL} <span className="arrow">→</span></a>
                  </>
                ) : (
                  <p className="apply-lede">{APPLY_PART_TIME_MESSAGE}</p>
                )}
              </div>
            ) : (
              <>
                <div className="gpanel-h"><b>{'0' + step}</b> {`Step ${step} of 4`}</div>
                <div className="gpanel-b">
                  <div className="apply-progress" aria-hidden="true"><span style={{ width: (step / 4) * 100 + '%' }}></span></div>

                  {step === 1 && (
                    <div className="apply-step">
                      <h3>Where are you today?</h3>
                      <div className="apply-opts">
                        {APPLY_STATUS_OPTIONS.map((opt) => (
                          <button type="button" key={opt} className={choice(a.status === opt)} aria-pressed={a.status === opt} onClick={() => set('status', opt)}>{opt}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="apply-step">
                      <h3>Your last 12 months</h3>
                      <div className="gfield"><label>Closings</label>{seg(APPLY_CLOSINGS_OPTIONS, a.closings, (v) => set('closings', v))}</div>
                      <div className="gfield"><label>Volume</label>{seg(APPLY_VOLUME_OPTIONS, a.volume, (v) => set('volume', v))}</div>
                      <div className="gfield"><label>Full-time or part-time?</label>{seg(APPLY_WORKLOAD_OPTIONS, a.workload, (v) => set('workload', v))}</div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="apply-step">
                      <h3>Your path</h3>
                      <div className="apply-opts">
                        {APPLY_PATH_OPTIONS.map((opt) => (
                          <button type="button" key={opt.value} className={choice(a.path === opt.value)} aria-pressed={a.path === opt.value} onClick={() => set('path', opt.value)}>
                            {opt.title}<small>{opt.sub}</small>
                          </button>
                        ))}
                      </div>
                      <div className="gfield"><label>Languages</label>{seg(APPLY_LANGUAGE_OPTIONS, a.language, (v) => set('language', v))}</div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="apply-step">
                      <h3>How do we reach you?</h3>
                      <div className="gtwo">
                        <div className="gfield"><label htmlFor="ap_first">First name</label><input className="apply-input" id="ap_first" autoComplete="given-name" value={a.first} onChange={(e) => set('first', e.target.value)} /></div>
                        <div className="gfield"><label htmlFor="ap_last">Last name</label><input className="apply-input" id="ap_last" autoComplete="family-name" value={a.last} onChange={(e) => set('last', e.target.value)} /></div>
                      </div>
                      <div className="gfield"><label htmlFor="ap_phone">Mobile</label><input className="apply-input" id="ap_phone" type="tel" autoComplete="tel" value={a.phone} onChange={(e) => set('phone', e.target.value)} /></div>
                      <div className="gfield"><label htmlFor="ap_email">Email</label><input className="apply-input" id="ap_email" type="email" autoComplete="email" value={a.email} onChange={(e) => set('email', e.target.value)} /></div>
                      <div className="gfield"><label htmlFor="ap_brokerage">Current brokerage <span className="apply-hint">(optional, stays confidential)</span></label><input className="apply-input" id="ap_brokerage" autoComplete="organization" value={a.brokerage} onChange={(e) => set('brokerage', e.target.value)} /></div>
                      <label className="apply-consent">
                        <input type="checkbox" checked={a.smsConsent} onChange={(e) => set('smsConsent', e.target.checked)} />
                        <span>{APPLY_SMS_CONSENT_TEXT}</span>
                      </label>
                    </div>
                  )}

                  {error && <div className="gcap-msg err" role="alert">{error}</div>}

                  <div className="apply-nav">
                    {step > 1 && <button type="button" className="btn btn-ghost" onClick={() => { setError(''); setStep(step - 1) }} disabled={sending}>Back</button>}
                    {step < 4
                      ? <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)} disabled={!answered[step]}>Continue <span className="arrow">→</span></button>
                      : <button type="button" className="btn btn-primary" onClick={submit} disabled={!answered[4] || sending}>{sending ? 'Sending…' : 'Submit application'}</button>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap');

html { scroll-behavior: smooth; scroll-padding-top: 88px; }
@media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }

.rec-root {
  --ink:#08080A;
  --panel:#101014;
  --panel-2:#16161C;
  --line:#26262E;
  --line-soft:#1C1C22;
  --bone:#F4F3EF;
  --muted:#8C8C97;
  --muted-2:#5E5E68;
  --blue:#147EFB;
  --blue-deep:#0A4FA8;
  --blue-glow:rgba(20,126,251,.16);
  --gold:#FDBF00;
  --radius:16px;
  --maxw:1180px;
  --display:'Archivo',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  --body:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  --mono:'Space Mono','SFMono-Regular',Menlo,monospace;
  background:var(--ink);
  color:var(--bone);
  font-family:var(--body);
  line-height:1.6;
  -webkit-font-smoothing:antialiased;
  overflow-x:hidden;
  min-height:100vh;
}
.rec-root *{box-sizing:border-box;margin:0;padding:0}
.rec-root a{color:inherit;text-decoration:none}
.rec-root .wrap{max-width:var(--maxw);margin:0 auto;padding:0 24px}
.rec-root .eyebrow{
  font-family:var(--display);font-weight:700;font-size:12px;letter-spacing:.22em;
  text-transform:uppercase;color:var(--gold);display:inline-flex;align-items:center;gap:10px;
}
.rec-root .eyebrow::before{content:"";width:26px;height:1px;background:var(--gold);opacity:.7}
.rec-root h1,.rec-root h2,.rec-root h3{font-family:var(--display);line-height:1.02;letter-spacing:-.02em;font-weight:900;overflow-wrap:break-word;word-break:break-word}
.rec-root .section{padding:104px 0;position:relative}
.rec-root .section-sm{padding:72px 0}
.rec-root .divider{height:1px;background:var(--line-soft);border:0}

/* Buttons */
.rec-root .btn{
  display:inline-flex;align-items:center;justify-content:center;gap:9px;
  font-family:var(--display);font-weight:700;font-size:15px;letter-spacing:.01em;
  padding:15px 26px;border-radius:10px;cursor:pointer;border:1px solid transparent;
  transition:transform .16s ease, background .16s ease, border-color .16s ease, box-shadow .16s ease;
  white-space:nowrap;
}
.rec-root .btn-primary{background:var(--blue);color:#fff;box-shadow:0 8px 30px var(--blue-glow)}
.rec-root .btn-primary:hover{background:#2a8bff;transform:translateY(-2px);box-shadow:0 12px 38px var(--blue-glow)}
.rec-root .btn-gold{background:var(--gold);color:#0A0A0A}
.rec-root .btn-gold:hover{transform:translateY(-2px);filter:brightness(1.05)}
.rec-root .btn-ghost{background:transparent;color:var(--bone);border-color:var(--line)}
.rec-root .btn-ghost:hover{border-color:var(--bone);transform:translateY(-2px)}
.rec-root .btn-sm{padding:11px 18px;font-size:14px}
.rec-root .arrow{transition:transform .16s ease}
.rec-root .btn:hover .arrow{transform:translateX(3px)}

/* Header */
.rec-root header{
  position:sticky;top:0;z-index:60;
  background:rgba(8,8,10,.72);backdrop-filter:blur(14px);
  border-bottom:1px solid var(--line-soft);
}
.rec-root .nav{display:flex;align-items:center;gap:28px;height:72px}
.rec-root .logo{font-family:var(--display);font-weight:900;font-size:22px;letter-spacing:-.03em;display:flex;align-items:center;gap:9px}
.rec-root .logo b{color:var(--blue)}
.rec-root .logo .dot{width:9px;height:9px;border-radius:50%;background:var(--gold);display:inline-block;margin-bottom:2px}
.rec-root .logo-img{height:32px;width:auto;display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6))}
.rec-root .foot-logo-img{height:38px}
.rec-root .nav-links{display:flex;gap:26px;margin-left:14px}
.rec-root .nav-links a{font-size:14.5px;color:var(--muted);font-weight:500;transition:color .15s ease}
.rec-root .nav-links a:hover{color:var(--bone)}
.rec-root .nav-right{margin-left:auto;display:flex;align-items:center;gap:14px}
.rec-root .habla,.rec-root .member .chip{
  font-family:var(--display);font-weight:700;font-size:11px;letter-spacing:.14em;text-transform:uppercase;
  color:var(--gold);border:1px solid rgba(253,191,0,.3);border-radius:999px;padding:6px 13px;
}
.rec-root .menu-btn{display:none;background:none;border:1px solid var(--line);border-radius:9px;padding:9px 11px;cursor:pointer}
.rec-root .mobile-menu{display:none}
.rec-root .menu-btn span{display:block;width:20px;height:2px;background:var(--bone);margin:4px 0;transition:.2s}

/* Hero */
.rec-root .hero{position:relative;padding:118px 0 96px;overflow:hidden}
.rec-root .hero-grid-bg{
  position:absolute;inset:0;z-index:0;pointer-events:none;
  background-image:linear-gradient(var(--line-soft) 1px,transparent 1px),linear-gradient(90deg,var(--line-soft) 1px,transparent 1px);
  background-size:64px 64px;
  -webkit-mask-image:radial-gradient(ellipse 80% 60% at 70% 15%,#000 0%,transparent 72%);
  mask-image:radial-gradient(ellipse 80% 60% at 70% 15%,#000 0%,transparent 72%);
  opacity:.5;
}
.rec-root .hero-glow{
  position:absolute;top:-160px;right:-120px;width:620px;height:620px;z-index:0;pointer-events:none;
  background:radial-gradient(circle,var(--blue-glow) 0%,transparent 62%);
}
.rec-root .hero .wrap{position:relative;z-index:1}
.rec-root .hero h1{font-size:clamp(40px,6vw,80px);max-width:15ch;margin:22px 0 0}
.rec-root .hero h1 .swipe{position:relative;white-space:nowrap;color:var(--bone)}
.rec-root .hero h1 .swipe::after{
  content:"";position:absolute;left:-2px;right:-2px;bottom:.08em;height:.14em;background:var(--gold);
  transform:scaleX(0);transform-origin:left;animation:recSwipe 1s cubic-bezier(.2,.7,.2,1) .5s forwards;
}
@keyframes recSwipe{to{transform:scaleX(1)}}
@media (prefers-reduced-motion:reduce){.rec-root .hero h1 .swipe::after{transform:scaleX(1);animation:none}}
.rec-root .hero .lede{font-size:clamp(17px,2vw,20px);color:var(--muted);max-width:56ch;margin:26px 0 0;line-height:1.55}
.rec-root .hero .lede .es{color:var(--gold);font-style:italic;font-weight:500}
.rec-root .hero-cta{display:flex;flex-wrap:wrap;gap:14px;margin-top:34px}
.rec-root .trust{display:flex;flex-wrap:wrap;gap:14px 34px;margin-top:56px;padding-top:26px;border-top:1px solid var(--line-soft)}
.rec-root .trust span{display:flex;align-items:center;gap:9px;font-size:13.5px;color:var(--muted);font-weight:500}
.rec-root .trust b{color:var(--bone);font-family:var(--display);font-weight:800}
.rec-root .trust .tick{color:var(--blue)}
.rec-root .hero-inner{display:grid;grid-template-columns:1.2fr .8fr;gap:44px;align-items:center}
.rec-root .hero-left{min-width:0}
.rec-root .hero-visual{position:relative;height:452px;border-radius:20px;overflow:hidden;border:1px solid var(--line-soft);background:radial-gradient(130% 120% at 72% 22%,rgba(20,126,251,.12),transparent 58%),var(--panel)}
.rec-root .hero-visual canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.rec-root .hv-badge{position:absolute;left:16px;top:14px;z-index:2;font-family:var(--mono);font-size:10.5px;letter-spacing:.08em;color:var(--muted);display:flex;align-items:center;gap:8px}
.rec-root .hv-badge .live{width:7px;height:7px;border-radius:50%;background:#35c46a;animation:recHvlive 2s infinite}
@keyframes recHvlive{0%{box-shadow:0 0 0 0 rgba(53,196,106,.55)}70%{box-shadow:0 0 0 7px rgba(53,196,106,0)}100%{box-shadow:0 0 0 0 rgba(53,196,106,0)}}
@media (prefers-reduced-motion:reduce){.rec-root .hv-badge .live{animation:none}}
@media (max-width:940px){
  .rec-root .hero-inner{grid-template-columns:1fr;gap:36px}
  .rec-root .hero-visual{height:300px;order:2}
}

/* Section head */
.rec-root .head{max-width:70ch}
.rec-root .head h2{font-size:clamp(30px,4.4vw,50px);margin:18px 0 0}
.rec-root .head p{color:var(--muted);font-size:17px;margin-top:18px;max-width:60ch}

/* Two paths fork */
.rec-root .fork{display:grid;grid-template-columns:1fr auto 1fr;gap:0;margin-top:52px;align-items:stretch}
.rec-root .path{background:var(--panel);border:1px solid var(--line-soft);padding:36px 34px;display:flex;flex-direction:column}
.rec-root .path.left{border-radius:var(--radius) 0 0 var(--radius);border-right:0}
.rec-root .path.right{border-radius:0 var(--radius) var(--radius) 0;border-left:0;background:var(--panel-2)}
.rec-root .fork-split{width:64px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;position:relative}
.rec-root .fork-split .vline{position:absolute;top:0;bottom:0;width:1px;background:linear-gradient(var(--line-soft),var(--blue),var(--line-soft))}
.rec-root .fork-split .or{font-family:var(--display);font-weight:800;font-size:12px;letter-spacing:.1em;color:var(--muted);background:var(--ink);border:1px solid var(--line);border-radius:999px;width:44px;height:44px;display:flex;align-items:center;justify-content:center;z-index:1}
.rec-root .path .tag{font-family:var(--display);font-weight:700;font-size:12px;letter-spacing:.16em;text-transform:uppercase}
.rec-root .path.left .tag{color:var(--blue)}
.rec-root .path.right .tag{color:var(--gold)}
.rec-root .path h3{font-size:27px;margin:12px 0 4px}
.rec-root .path .who{color:var(--muted);font-size:14.5px;min-height:42px}
.rec-root .path ul{list-style:none;margin:22px 0 26px;display:flex;flex-direction:column;gap:12px}
.rec-root .path li{display:flex;gap:11px;font-size:15px;color:#D9D9DE;align-items:flex-start}
.rec-root .path li svg{flex:none;margin-top:3px}
.rec-root .path .foot{margin-top:auto}
.rec-root .path .btn{width:100%}

/* Card grid */
.rec-root .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:52px}
.rec-root .card{background:var(--panel);border:1px solid var(--line-soft);border-radius:var(--radius);padding:30px 28px;transition:border-color .2s ease, transform .2s ease;position:relative;overflow:hidden}
.rec-root .card:hover{border-color:var(--line);transform:translateY(-3px)}
.rec-root .card .ic{width:46px;height:46px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:var(--blue-glow);color:var(--blue);margin-bottom:20px}
.rec-root .card h3{font-size:19px;font-weight:800;letter-spacing:-.01em}
.rec-root .card p{color:var(--muted);font-size:14.5px;margin-top:10px;line-height:1.55}
.rec-root .card .es{color:var(--gold);font-weight:600;font-style:italic}

/* Technology section + two-brain diagram */
.rec-root .tech-sec{background:linear-gradient(180deg,#0B0B0F,var(--ink))}
.rec-root .brain{position:relative;margin:44px 0 8px;display:grid;grid-template-columns:1fr 1.05fr 1fr;gap:0;align-items:center;min-height:360px}
.rec-root .brain-links{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0}
.rec-root .brain-col{position:relative;z-index:1;display:flex;flex-direction:column;gap:20px;justify-content:center}
.rec-root .brain-core{align-items:center}
.rec-root .brain-out{align-items:flex-end}
.rec-root .bnode{background:var(--panel-2);border:1px solid var(--line);border-radius:14px;padding:15px 17px;max-width:280px;transition:border-color .3s ease,box-shadow .3s ease}
.rec-root .bn-h{display:flex;align-items:center;gap:9px;font-family:var(--display);font-weight:700;font-size:13.5px;color:var(--bone)}
.rec-root .bn-s{font-size:11.5px;color:var(--muted);margin-top:6px;line-height:1.45}
.rec-root .bdot{width:9px;height:9px;border-radius:50%;flex:none}
.rec-root .bdot.g{background:var(--gold)}
.rec-root .bdot.b{background:var(--blue)}
.rec-root .bdot.live{background:#35c46a;animation:recBlive 2s infinite}
@keyframes recBlive{0%{box-shadow:0 0 0 0 rgba(53,196,106,.5)}70%{box-shadow:0 0 0 8px rgba(53,196,106,0)}100%{box-shadow:0 0 0 0 rgba(53,196,106,0)}}
.rec-root .bcore{position:relative;width:158px;height:158px;border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;background:radial-gradient(circle,rgba(20,126,251,.20),rgba(11,11,15,.92));border:1.5px solid var(--blue)}
.rec-root .bcore.pulse{animation:recBpulse .7s ease}
@keyframes recBpulse{0%{box-shadow:0 0 0 0 rgba(20,126,251,.5)}100%{box-shadow:0 0 0 36px rgba(20,126,251,0)}}
.rec-root .bcore-ring{position:absolute;inset:-15px;border-radius:50%;border:1px dashed rgba(20,126,251,.32)}
.rec-root .bcore.spin .bcore-ring{animation:recBspin 20s linear infinite}
@keyframes recBspin{to{transform:rotate(360deg)}}
.rec-root .bcore-label{font-family:var(--display);font-weight:900;font-size:18px;color:var(--bone);line-height:1}
.rec-root .bcore-sub{font-family:var(--mono);font-size:9.5px;color:var(--blue);text-transform:uppercase;letter-spacing:.09em;margin-top:5px}
.rec-root .bout .brep{display:flex;flex-direction:column;gap:5px;margin-bottom:12px}
.rec-root .brep-l{height:5px;border-radius:3px;background:var(--line);width:100%;transition:background .4s ease}
.rec-root .brep-l.s{width:58%}
.rec-root .bnode.lit{border-color:var(--gold);box-shadow:0 0 26px rgba(253,191,0,.14)}
.rec-root .bnode.lit .brep-l{background:linear-gradient(90deg,var(--gold),var(--blue))}
.rec-root .brain-cap{text-align:center;font-family:var(--mono);font-size:11.5px;color:var(--muted-2);margin-top:12px;letter-spacing:.03em;line-height:1.5}
.rec-root .brain-cap b{color:var(--muted)}
.rec-root .bcore-tag{margin-top:14px;font-family:var(--mono);font-size:10.5px;letter-spacing:.03em;color:var(--muted);display:flex;align-items:center;gap:7px;justify-content:center;text-align:center}
.rec-root .bcore-tag .ad{width:6px;height:6px;border-radius:50%;background:var(--gold);flex:none}
.rec-root .brain-links circle{will-change:transform}
@media (max-width:820px){
  .rec-root .brain{grid-template-columns:1fr;gap:30px;min-height:0;margin-top:36px}
  .rec-root .brain-links{display:block;z-index:2}
  .rec-root .brain-col{gap:30px}
  .rec-root .brain-in .bnode,.rec-root .brain-out .bnode{margin:0 auto;max-width:340px;width:100%}
  .rec-root .brain-out{align-items:center}
  .rec-root .bnode{max-width:340px}
  .rec-root .brain-core{margin:2px 0}
}

/* Tool feature blocks + browser mockups */
.rec-root .tools{display:flex;flex-direction:column;gap:26px;margin-top:52px}
/* Centred under the tool cards, matching the gap between them so it reads as
   the next item in the stack rather than a stray control. */
.rec-root .tools-more{display:flex;justify-content:center;flex-wrap:wrap;gap:14px;margin-top:26px}
/* The full toolkit: spacing only. Cards, grid and labels are the existing styles. */
.rec-root .toolkit{margin-top:72px}
.rec-root .toolkit-group{margin-top:36px}
.rec-root .toolkit .grid-3{margin-top:16px}
.rec-root .toolkit .card.wide{grid-column:span 2}
@media (max-width:900px){.rec-root .toolkit .card.wide{grid-column:auto}}
.rec-root .tool{display:grid;grid-template-columns:1.02fr .98fr;gap:44px;align-items:center;background:var(--panel);border:1px solid var(--line-soft);border-radius:20px;padding:42px}
.rec-root .tool-copy,.rec-root .tool-visual{min-width:0}
.rec-root .tool.reverse .tool-copy{order:2}
.rec-root .tool.reverse .tool-visual{order:1}
.rec-root .tool-num{font-family:var(--mono);font-size:13px;letter-spacing:.05em;color:var(--muted);text-transform:uppercase}
.rec-root .tool h3{font-size:clamp(23px,3vw,31px);margin:14px 0 0;line-height:1.1}
.rec-root .tool h3 em{color:var(--gold);font-style:italic;font-weight:800}
.rec-root .tool .one{font-size:16px;color:#E6E6EA;margin-top:16px;line-height:1.55}
.rec-root .tool .one b{color:var(--blue)}
.rec-root .tool .body{color:var(--muted);font-size:14.5px;margin-top:14px;line-height:1.62}
.rec-root .tool ul{list-style:none;margin:20px 0 26px;display:flex;flex-direction:column;gap:9px}
.rec-root .tool li{font-size:14px;color:#D4D4DA;display:flex;gap:9px;align-items:flex-start}
.rec-root .tool li .ck{color:var(--blue);font-weight:800;flex:none}
.rec-root .tool.reverse li .ck{color:var(--gold)}
.rec-root .browser{border:1px solid var(--line);border-radius:14px;overflow:hidden;background:#0C0C11;box-shadow:0 24px 60px rgba(0,0,0,.45)}
.rec-root .browser-bar{display:flex;align-items:center;gap:8px;padding:12px 14px;border-bottom:1px solid var(--line-soft);background:#101016}
.rec-root .browser-bar .d{width:11px;height:11px;border-radius:50%;flex:none}
.rec-root .browser-bar .r{background:#ff5f57}
.rec-root .browser-bar .y{background:#febc2e}
.rec-root .browser-bar .g{background:#28c840}
.rec-root .browser-bar .url{flex:1;min-width:0;margin-left:8px;font-family:var(--mono);font-size:12px;color:var(--muted);background:var(--ink);border:1px solid var(--line-soft);border-radius:7px;padding:6px 12px;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rec-root .browser-body{padding:22px;background:radial-gradient(120% 100% at 100% 0,rgba(20,126,251,.06),transparent 55%)}
.rec-root .mock{background:var(--panel-2);border:1px solid var(--line-soft);border-radius:12px;padding:20px}
.rec-root .mock-rhead{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--line-soft);padding-bottom:12px;gap:10px}
.rec-root .mock-brand{font-family:var(--display);font-weight:900;font-size:15px;white-space:nowrap}
.rec-root .mock-brand .mdot{display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--gold);margin-left:3px;vertical-align:middle}
.rec-root .mock-tag{font-family:var(--mono);font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.04em;text-align:right}
.rec-root .mock-subj{font-size:12px;color:var(--muted);margin:13px 0 12px;font-family:var(--mono)}
.rec-root .mock-rows{display:flex;flex-direction:column;gap:8px}
.rec-root .mock-row{display:flex;justify-content:space-between;gap:12px;font-size:12.5px;color:#D4D4DA;padding:9px 12px;background:var(--ink);border-radius:8px}
.rec-root .mock-row b{font-family:var(--mono);color:var(--bone);white-space:nowrap}
.rec-root .mock-reco{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:12px;padding:12px 14px;background:var(--blue-glow);border:1px solid rgba(20,126,251,.32);border-radius:9px}
.rec-root .mock-reco span{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.07em;font-family:var(--display);font-weight:700}
.rec-root .mock-reco b{font-family:var(--mono);font-size:19px;color:var(--gold);white-space:nowrap}
.rec-root .mock-note{font-size:10.5px;color:var(--muted-2);margin-top:12px;font-family:var(--mono);text-align:center}
.rec-root .mock-items{display:flex;flex-direction:column;gap:11px;margin-top:14px}
.rec-root .mock-item{display:flex;gap:11px;align-items:flex-start}
.rec-root .mock-item .mk{flex:none;width:22px;height:22px;border-radius:6px;background:var(--blue);color:#fff;font-family:var(--display);font-weight:800;font-size:12px;display:flex;align-items:center;justify-content:center;margin-top:1px}
.rec-root .mock-item p{margin:0;font-size:12.5px;color:#D4D4DA;line-height:1.45}

/* Application (#apply). Reuses .gpanel, .gfield, .gseg, .gtwo, .btn and
   .eyebrow; these rules are layout and the selected/disabled states only. */
.rec-root .apply-grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:44px;align-items:start}
.rec-root .apply-intro h2{font-size:clamp(30px,4.4vw,50px);margin:18px 0 0}
.rec-root .apply-next{list-style:none;margin:28px 0 0;display:flex;flex-direction:column;gap:14px}
.rec-root .apply-next li{display:flex;gap:14px;align-items:baseline;color:#D9D9DE;font-size:15px}
.rec-root .apply-next b{font-family:var(--mono);color:var(--blue);font-size:13px;flex:none}
.rec-root .apply-conf{margin-top:26px;padding-top:20px;border-top:1px solid var(--line-soft);color:var(--muted);font-size:14px}
.rec-root .apply-progress{height:4px;border-radius:2px;background:var(--line);overflow:hidden}
.rec-root .apply-progress span{display:block;height:100%;background:var(--blue);transition:width .25s ease}
.rec-root .apply-step{display:flex;flex-direction:column;gap:14px}
.rec-root .apply-step h3{font-size:22px;font-weight:800}
.rec-root .apply-opts{display:flex;flex-direction:column;gap:10px}
.rec-root .apply-opt{display:block;width:100%;text-align:left;background:var(--ink);border:1px solid var(--line);border-radius:12px;padding:14px 16px;color:var(--bone);font-family:var(--display);font-weight:700;font-size:15px;cursor:pointer;transition:border-color .15s ease,background .15s ease}
.rec-root .apply-opt:hover{border-color:var(--muted-2)}
.rec-root .apply-opt.on{border-color:var(--blue);background:var(--blue-glow)}
.rec-root .apply-opt small{display:block;font-family:var(--body);font-weight:400;font-size:13px;color:var(--muted);margin-top:3px}
.rec-root .apply-seg{flex-wrap:wrap}
.rec-root .apply-seg button{font-size:12px;padding:10px 8px}
.rec-root .apply-input{width:100%;background:var(--ink);border:1px solid var(--line);border-radius:9px;color:var(--bone);font-family:var(--body);font-size:16px;padding:12px 14px;outline:none}
.rec-root .apply-input:focus{border-color:var(--blue)}
.rec-root .apply-hint{font-weight:400;color:var(--muted-2)}
.rec-root .apply-consent{display:flex;gap:10px;align-items:flex-start;font-size:12px;line-height:1.5;color:var(--muted);cursor:pointer}
.rec-root .apply-consent input{margin-top:3px;flex:none;accent-color:var(--blue)}
.rec-root .apply-nav{display:flex;gap:10px;justify-content:flex-end;margin-top:6px}
.rec-root .apply-nav .btn[disabled]{opacity:.45;cursor:not-allowed;transform:none;box-shadow:none}
.rec-root .apply-result{gap:16px}
.rec-root .apply-result h3{font-size:26px;font-weight:900}
.rec-root .apply-result .btn{align-self:flex-start}
.rec-root .apply-lede{color:#D9D9DE;font-size:16px;line-height:1.55}
.rec-root .path .foot{display:flex;flex-direction:column;gap:10px}
@media (max-width:900px){.rec-root .apply-grid{grid-template-columns:1fr;gap:32px}}
@media (max-width:560px){.rec-root .nav-call{display:none}}

/* Lead-gen calculator */
.rec-root .calc-sec{background:linear-gradient(180deg,var(--ink),#0B0B0F)}
.rec-root .gcalc{margin-top:48px;display:grid;grid-template-columns:repeat(3,1fr);gap:18px;align-items:start}
.rec-root .gpanel{background:var(--panel);border:1px solid var(--line-soft);border-radius:18px;overflow:hidden;display:flex;flex-direction:column}
.rec-root .gpanel-h{padding:15px 22px;border-bottom:1px solid var(--line-soft);font-family:var(--display);font-weight:800;font-size:12px;letter-spacing:.13em;text-transform:uppercase;color:var(--muted)}
.rec-root .gpanel-h b{color:var(--blue);margin-right:8px;font-family:var(--mono)}
.rec-root .gpanel-b{padding:22px;display:flex;flex-direction:column;gap:14px}
.rec-root .gfield{display:flex;flex-direction:column;gap:7px}
.rec-root .gfield label{font-size:12px;font-weight:600;color:var(--muted);letter-spacing:.01em}
.rec-root .grow{display:flex;align-items:center;gap:9px;background:var(--ink);border:1px solid var(--line);border-radius:9px;padding:0 13px;transition:border-color .15s ease}
.rec-root .grow:focus-within{border-color:var(--blue)}
.rec-root .grow .sym{font-family:var(--mono);color:var(--muted-2);font-size:14px}
.rec-root .grow input{flex:1;min-width:0;width:100%;background:none;border:0;color:var(--bone);font-family:var(--mono);font-size:16px;font-weight:700;padding:11px 0;outline:none}
.rec-root .grow input::-webkit-outer-spin-button,.rec-root .grow input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.rec-root .grow input[type=number]{-moz-appearance:textfield}
.rec-root .gtwo{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.rec-root .gseg{display:flex;background:var(--ink);border:1px solid var(--line);border-radius:9px;padding:3px;gap:3px}
.rec-root .gseg button{flex:1;background:none;border:0;color:var(--muted);font-family:var(--display);font-weight:700;font-size:11px;letter-spacing:.01em;padding:9px 5px;border-radius:6px;cursor:pointer;transition:.15s}
.rec-root .gseg button.on{background:var(--blue);color:#fff}
.rec-root .gactuals{display:none;flex-direction:column;gap:12px;border-left:2px solid var(--gold);padding-left:14px}
.rec-root .gactuals.show{display:flex}
.rec-root .gbtn-sm{background:var(--blue);color:#fff;border:0;border-radius:8px;font-family:var(--display);font-weight:700;font-size:13px;padding:10px 14px;cursor:pointer;transition:.15s}
.rec-root .gbtn-sm:hover{background:#2a8bff}
.rec-root .gsmall{font-family:var(--mono);font-size:11px;color:var(--muted-2);line-height:1.4}
.rec-root .gout{padding:17px 20px;border:1px solid var(--line-soft);border-left-width:3px;border-radius:10px;background:var(--ink)}
.rec-root .gout .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.07em;font-family:var(--display);font-weight:700}
.rec-root .gout .v{font-family:var(--mono);font-weight:700;font-size:31px;line-height:1;margin-top:7px;color:var(--bone)}
.rec-root .gout.b1{border-left-color:var(--blue)} .rec-root .gout.b1 .v{color:var(--blue)}
.rec-root .gout.b2{border-left-color:var(--gold)} .rec-root .gout.b2 .v{color:var(--gold)}
.rec-root .gout.b3{border-left-color:#35c46a} .rec-root .gout.b3 .v{color:#4ad07d}
.rec-root details.gnote{background:rgba(253,191,0,.05);border:1px solid rgba(253,191,0,.18);border-radius:9px;padding:12px 14px}
.rec-root details.gnote summary{cursor:pointer;font-size:12.5px;font-weight:700;color:#E4E4E8;font-family:var(--display);letter-spacing:.01em}
.rec-root details.gnote ul{margin:11px 0 0 15px;display:flex;flex-direction:column;gap:6px}
.rec-root details.gnote li{font-size:12px;color:var(--muted);line-height:1.45}
.rec-root details.gnote b{color:#D4D4DA}
.rec-root .gpie-wrap{display:flex;align-items:center;justify-content:center;padding:4px 0 12px}
.rec-root .gpie{width:150px;height:150px;transform:rotate(-90deg)}
.rec-root .glegend{display:flex;flex-wrap:wrap;gap:7px 12px;justify-content:center;margin-bottom:10px}
.rec-root .glegend span{display:flex;align-items:center;gap:6px;font-size:11px;color:var(--muted)}
.rec-root .glegend i{width:10px;height:10px;border-radius:3px;display:inline-block;flex:none}
.rec-root .gsrc{display:grid;grid-template-columns:1fr auto;gap:7px 10px;align-items:center;padding:11px 0;border-bottom:1px solid var(--line-soft)}
.rec-root .gsrc:last-child{border-bottom:0}
.rec-root .gsrc .nm{font-size:12.5px;font-weight:600;color:#D9D9DE}
.rec-root .gsrc .cv{display:flex;align-items:center;gap:5px}
.rec-root .gsrc .cv input{width:44px;background:var(--ink);border:1px solid var(--line);border-radius:6px;color:var(--bone);font-family:var(--mono);font-size:12px;text-align:right;padding:5px 6px;outline:none}
.rec-root .gsrc .cv input:focus{border-color:var(--blue)}
.rec-root .gsrc .cv span{font-size:10px;color:var(--muted-2);font-family:var(--mono)}
.rec-root .gsrc input[type=range]{grid-column:1/-1;width:100%;accent-color:var(--blue);height:4px;cursor:pointer}
.rec-root .gother{display:flex;justify-content:space-between;font-size:12px;color:var(--muted);margin-top:12px;font-family:var(--mono)}
.rec-root .gcapture{margin-top:20px;background:var(--panel);border:1px solid var(--line);border-radius:16px;padding:26px 28px;display:grid;grid-template-columns:1fr auto;gap:24px;align-items:center}
.rec-root .gcapture h3{font-size:21px;font-weight:800}
.rec-root .gcapture p{color:var(--muted);font-size:13.5px;margin-top:6px;max-width:42ch}
.rec-root .gcap-form{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end}
.rec-root .gcap-form input{background:var(--ink);border:1px solid var(--line);border-radius:9px;color:var(--bone);font-size:14px;padding:12px 14px;outline:none;min-width:150px}
.rec-root .gcap-form input:focus{border-color:var(--blue)}
.rec-root .gcap-msg{font-size:13px;margin-top:10px;font-weight:600;min-height:18px}
.rec-root .gcap-msg.ok{color:#4ad07d} .rec-root .gcap-msg.err{color:#ff6b6b}
.rec-root .calc-cta{margin-top:26px;display:flex;align-items:center;justify-content:center;gap:16px;flex-wrap:wrap}
.rec-root .calc-cta-or{color:var(--muted);font-size:14.5px}

/* Plans */
.rec-root .plans{display:grid;grid-template-columns:repeat(2,1fr);gap:18px;margin-top:52px;max-width:780px;margin-left:auto;margin-right:auto}
.rec-root .plan{background:var(--panel);border:1px solid var(--line-soft);border-radius:var(--radius);padding:32px 30px;display:flex;flex-direction:column;position:relative}
.rec-root .plan.feature{border-color:var(--blue);background:linear-gradient(180deg,rgba(20,126,251,.06),var(--panel))}
.rec-root .plan .pk{font-family:var(--display);font-weight:700;font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted)}
.rec-root .plan.feature .pk{color:var(--blue)}
.rec-root .plan h3{font-size:24px;margin:14px 0 0}
.rec-root .plan .sub{color:var(--muted);font-size:14px;margin-top:8px;min-height:60px}
.rec-root .plan .line{height:1px;background:var(--line-soft);margin:22px 0}
.rec-root .plan ul{list-style:none;display:flex;flex-direction:column;gap:11px;margin-bottom:26px}
.rec-root .plan li{font-size:14.5px;color:#D4D4DA;display:flex;gap:10px;align-items:flex-start}
.rec-root .plan li svg{flex:none;margin-top:3px}
.rec-root .plan .btn{width:100%;margin-top:auto}
.rec-root .badge{position:absolute;top:18px;right:18px;font-family:var(--display);font-weight:700;font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:#0A0A0A;background:var(--gold);border-radius:999px;padding:5px 11px}

/* Leadership / team */
.rec-root .team{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:52px}
.rec-root .member{background:var(--panel);border:1px solid var(--line-soft);border-radius:var(--radius);padding:30px 28px}
.rec-root .member .mono{width:100%;aspect-ratio:1.5/1;border-radius:12px;margin-bottom:22px;background:radial-gradient(circle at 30% 30%,rgba(20,126,251,.22),transparent 55%),linear-gradient(135deg,#15151B,#0C0C10);border:1px solid var(--line-soft);display:flex;align-items:flex-end;padding:18px;position:relative;overflow:hidden}
.rec-root .member .mono .init{font-family:var(--display);font-weight:900;font-size:52px;color:rgba(244,243,239,.14);letter-spacing:-.03em;line-height:.8}
.rec-root .member .mono .kx{position:absolute;top:14px;right:16px;font-family:var(--mono);font-size:11px;color:var(--muted-2)}
/* Leadership photos: 800x800 head-and-shoulders crops, eyes level across all three. */
.rec-root .member .photo-wrap{position:relative;margin-bottom:22px}
.rec-root .member picture{display:block}
/* Credential chips reuse the .habla pill (above); this only lays them out. */
.rec-root .member .chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}
.rec-root .member .chip{line-height:1.35}
/* Certification seal overlapping the photo's bottom-left corner. */
.rec-root .member .cred-badge{position:absolute;left:-10px;bottom:-10px;width:96px;height:96px;filter:drop-shadow(0 4px 10px rgba(0,0,0,.35))}
@media (max-width:900px){.rec-root .member .cred-badge{width:72px;height:72px}}
.rec-root .member .photo{display:block;width:100%;height:auto;aspect-ratio:1/1;object-fit:cover;border-radius:12px;border:1px solid var(--line-soft);background:var(--panel-2)}
.rec-root .member h3{font-size:20px;font-weight:800}
.rec-root .member .role{color:var(--blue);font-family:var(--display);font-weight:700;font-size:12.5px;letter-spacing:.06em;text-transform:uppercase;margin-top:6px}
.rec-root .member p{color:var(--muted);font-size:14px;margin-top:14px;line-height:1.55}

/* Testimonials */
.rec-root .quotes{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:52px}
.rec-root .quote{background:var(--panel);border:1px solid var(--line-soft);border-radius:var(--radius);padding:34px 32px}
.rec-root .quote .mark{font-family:var(--display);font-weight:900;font-size:56px;color:var(--blue);line-height:.6;height:26px}
.rec-root .quote p{font-size:16px;color:#E4E4E8;line-height:1.6;margin:8px 0 20px}
.rec-root .quote .by{display:flex;align-items:center;gap:12px}
.rec-root .quote .av{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,var(--blue),var(--blue-deep));display:flex;align-items:center;justify-content:center;font-family:var(--display);font-weight:800;font-size:15px;color:#fff}
.rec-root .quote .by b{font-family:var(--display);font-weight:700;font-size:14.5px;display:block}
.rec-root .quote .by span{font-size:12.5px;color:var(--muted)}

/* License band */
.rec-root .band{background:linear-gradient(135deg,#0C1830,var(--panel));border:1px solid var(--line);border-radius:20px;padding:44px 42px;display:grid;grid-template-columns:1fr auto;gap:32px;align-items:center}
.rec-root .band .off{font-family:var(--display);font-weight:900;font-size:clamp(40px,6vw,64px);color:var(--gold);line-height:.9;letter-spacing:-.03em;margin-top:14px}
.rec-root .band h3{font-size:26px;margin:6px 0 0}
.rec-root .band p{color:var(--muted);font-size:15px;margin-top:12px;max-width:52ch}
.rec-root .band .actions{display:flex;flex-direction:column;gap:12px;min-width:230px}
.rec-root .band .actions .btn{width:100%}

/* FAQ accordion */
.rec-root .faq{margin-top:48px;border-top:1px solid var(--line-soft)}
.rec-root .q{border-bottom:1px solid var(--line-soft)}
.rec-root .q button{width:100%;background:none;border:0;cursor:pointer;color:var(--bone);text-align:left;display:flex;justify-content:space-between;align-items:center;gap:20px;padding:24px 4px;font-family:var(--display);font-weight:700;font-size:18px;letter-spacing:-.01em}
.rec-root .q .plus{flex:none;width:24px;height:24px;position:relative;transition:transform .25s ease}
.rec-root .q .plus::before,.rec-root .q .plus::after{content:"";position:absolute;background:var(--blue);border-radius:2px}
.rec-root .q .plus::before{top:11px;left:2px;right:2px;height:2px}
.rec-root .q .plus::after{left:11px;top:2px;bottom:2px;width:2px;transition:opacity .25s ease}
.rec-root .q[data-open="true"] .plus{transform:rotate(180deg)}
.rec-root .q[data-open="true"] .plus::after{opacity:0}
.rec-root .q .ans{max-height:0;overflow:hidden;transition:max-height .3s ease}
.rec-root .q .ans p{color:var(--muted);font-size:15.5px;line-height:1.6;padding:0 4px 26px;max-width:76ch}
.rec-root .q .ans .es{color:var(--gold);font-style:italic}

/* Final CTA */
.rec-root .final{position:relative;overflow:hidden;background:var(--panel);border-top:1px solid var(--line-soft);border-bottom:1px solid var(--line-soft)}
.rec-root .final .glow{position:absolute;inset:0;background:radial-gradient(circle at 50% 120%,var(--blue-glow),transparent 60%);pointer-events:none}
.rec-root .final .wrap{position:relative;text-align:center}
.rec-root .final h2{font-size:clamp(34px,5.6vw,64px);max-width:16ch;margin:16px auto 0}
.rec-root .final p{color:var(--muted);font-size:18px;margin:20px auto 0;max-width:52ch}
.rec-root .final .es{color:var(--gold);font-style:italic}
.rec-root .final .final-cta{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;margin-top:34px}

/* Footer */
.rec-root footer{background:var(--ink);padding:64px 0 40px}
.rec-root .foot-top{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:32px;padding-bottom:44px;border-bottom:1px solid var(--line-soft)}
.rec-root .foot-brand .logo{margin-bottom:16px}
.rec-root .foot-brand p{color:var(--muted);font-size:14px;max-width:34ch;line-height:1.55}
.rec-root .foot-brand .socials{display:flex;gap:10px;margin-top:20px}
.rec-root .foot-brand .socials a{width:38px;height:38px;border:1px solid var(--line);border-radius:9px;display:flex;align-items:center;justify-content:center;color:var(--muted);transition:.15s}
.rec-root .foot-brand .socials a:hover{color:var(--bone);border-color:var(--bone)}
.rec-root .foot-col h4{font-family:var(--display);font-weight:700;font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted-2);margin-bottom:16px}
.rec-root .foot-col a,.rec-root .foot-col span{display:block;color:var(--muted);font-size:14px;margin-bottom:11px;transition:color .15s ease}
.rec-root .foot-col a:hover{color:var(--bone)}
.rec-root .compliance{padding-top:28px;display:grid;grid-template-columns:1fr auto;gap:20px;align-items:start}
.rec-root .compliance .legal{font-size:12px;color:var(--muted-2);line-height:1.7;max-width:80ch}
.rec-root .compliance .legal b{color:var(--muted);font-weight:600}
.rec-root .compliance .copy{font-size:12px;color:var(--muted-2);text-align:right;white-space:nowrap}

/* Scroll reveal */
.rec-root .reveal{opacity:0;transform:translateY(22px);transition:opacity .6s ease,transform .6s ease}
.rec-root .reveal.in{opacity:1;transform:none}
@media (prefers-reduced-motion:reduce){.rec-root .reveal{opacity:1;transform:none;transition:none}}

/* Responsive header */
@media (max-width:900px){
  .rec-root .nav-links{display:none}
  .rec-root .habla{display:none}
  .rec-root .menu-btn{display:block}
  .rec-root .mobile-menu{display:none;flex-direction:column;gap:4px;padding:12px 24px 20px;border-bottom:1px solid var(--line-soft);background:var(--ink)}
  .rec-root .mobile-menu.open{display:flex}
  .rec-root .mobile-menu a{padding:12px 0;color:var(--muted);border-bottom:1px solid var(--line-soft);font-weight:500}
  .rec-root .grid-3{grid-template-columns:1fr}
  .rec-root .plans{grid-template-columns:1fr}
  .rec-root .team{grid-template-columns:1fr}
  .rec-root .quotes{grid-template-columns:1fr}
  .rec-root .band{grid-template-columns:1fr;padding:34px 28px}
  .rec-root .band .actions{min-width:0}
  .rec-root .gcalc{grid-template-columns:1fr}
  .rec-root .gcapture{grid-template-columns:1fr}
  .rec-root .gcap-form{justify-content:stretch}
  .rec-root .gcap-form input{flex:1 1 140px}
  .rec-root .tool{grid-template-columns:1fr;gap:26px;padding:28px}
  .rec-root .tool.reverse .tool-copy{order:1}
  .rec-root .tool.reverse .tool-visual{order:2}
  .rec-root .fork{grid-template-columns:1fr;gap:14px}
  .rec-root .fork-split{flex-direction:row;width:auto;height:40px}
  .rec-root .fork-split .vline{top:auto;bottom:auto;left:0;right:0;height:1px;width:100%}
  .rec-root .path.left,.rec-root .path.right{border-radius:var(--radius);border:1px solid var(--line-soft)}
  .rec-root .foot-top{grid-template-columns:1fr 1fr;gap:28px}
  .rec-root .foot-brand{grid-column:1/-1}
  .rec-root .compliance{grid-template-columns:1fr}
  .rec-root .compliance .copy{text-align:left}
  .rec-root .section{padding:76px 0}
}
@media (max-width:560px){
  .rec-root .foot-top{grid-template-columns:1fr}
  .rec-root .hero-cta .btn,.rec-root .final-cta .btn{width:100%}
  /* Shrink headline floors so long words wrap and fit within phone widths */
  .rec-root .hero h1{font-size:clamp(28px,8vw,42px)}
  .rec-root .head h2{font-size:clamp(23px,6.2vw,32px)}
  .rec-root .tool h3{font-size:clamp(19px,5.2vw,24px)}
  .rec-root .final h2{font-size:clamp(27px,7.4vw,50px)}
  .rec-root .plan h3,.rec-root .quote p{font-size:18px}
}
`
